import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ChevronDown, ChevronRight, Clock, Loader2, X, MessageCircle, ChevronLeft, Scissors, Search, ExternalLink, Link2 } from 'lucide-react';
import { useAuth } from '../services/AuthContext';
import { openRouterService, ModelPersistence } from '../services/openRouterService';
import ChatInterface from './ChatInterface';
import OrphanSessionRecovery from './OrphanSessionRecovery';
import OverflowBounceText from './OverflowBounceText';
import { DeepgramResponse, SpeakerLabels } from '../types';
import { useTopicVisualization, getRetentionColor } from '../contexts/TopicVisualizationContext';
import { useAudio } from '../contexts/AudioContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAutoScroll } from '../contexts/AutoScrollContext';
import { useAudioUI } from '../contexts/AudioUIContext';
import { useShareLink } from '../contexts/ShareLinkContext';
import CommentPopup from './CommentPopup';
import { useDevicePerformance } from '../hooks/useDevicePerformance';
import { OptimizedHighlightedText } from './OptimizedHighlightedText';
import { TypographyConfig, fontFamilies, maxWidths, defaultTypographyConfig } from './TypographySettings';
import { getSpeakerColorValue } from '../utils/speakerColors';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' 
  : 'http://localhost:7777';
const SMALL_SEGMENT_GAP_SECONDS = 2;

function toPlainMetadataText(value?: string | null, preserveLineBreaks = false): string {
  if (!value) return '';
  if (typeof document === 'undefined') {
    return String(value).trim();
  }

  let decoded = String(value);
  for (let iteration = 0; iteration < 2; iteration += 1) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = decoded;
    const next = textarea.value;
    if (next === decoded) break;
    decoded = next;
  }

  const container = document.createElement('div');
  container.innerHTML = decoded;
  const text = (container.textContent || container.innerText || '').trim();
  if (preserveLineBreaks) {
    return text
      .split('\n')
      .map((line) => line.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join('\n');
  }
  return text.replace(/\s+/g, ' ').trim();
}

function extractTopicDescription(value?: string | null): string | null {
  const normalized = String(value || '').replace(/<br\s*\/?>/gi, '\n');
  const justificationMatch = normalized.match(/Justification:\s*([\s\S]*?)(?:\n(?:Optimal Placement:|Continuity:|Score:)|Optimal Placement:|Continuity:|$)/i);
  const fallback = justificationMatch ? justificationMatch[1] : normalized;
  const cleaned = toPlainMetadataText(
    String(fallback || '')
      .replace(/Optimal Placement:.*$/i, '')
      .replace(/Continuity:.*$/i, '')
  );
  return cleaned || null;
}

interface ReadableTranscriptViewProps {
  ttmlContent: string;
  transcript: string;
  filename: string;
  transcriptionData?: DeepgramResponse;
  speakerLabels?: SpeakerLabels;
  speakerSegments?: Map<number, any[]>;
  audioFile?: File;
  transcriptionChecksum?: string;
  sessionId?: string;
  isModal?: boolean;
  onClose?: () => void;
  onPlaySegment?: (startTime: number, endTime?: number) => void;
  onPauseSegment?: () => void;
  showChat?: boolean;
  comments?: any[];
  isFullscreenTab?: boolean;
  selectedTtmlId?: string | null;
  searchQuery?: string;
  searchMatches?: any[];
  currentMatchIndex?: number;
  onSearchUpdate?: (matches: any[]) => void;
  shouldNavigateToMatch?: boolean;
  onNavigationComplete?: () => void;
  onCurrentTopicChange?: (title: string | null) => void;
  showVideoPanel?: boolean;
  typographyConfig?: TypographyConfig;
}

interface TranscriptWord {
  word: string;
  start: number;
  end: number;
  speaker: number;
  utteranceIndex: number;
  wordIndex: number;
}

function buildTranscriptWordKey(word: Pick<TranscriptWord, 'start' | 'end' | 'utteranceIndex' | 'wordIndex'>): string {
  return `${word.start}|${word.end}|${word.utteranceIndex}|${word.wordIndex}`;
}

interface TranscriptSegment {
  speaker: string;
  text: string;
  timestamp: string;
  timestampSeconds: number;
  endTimestampSeconds: number;
  words: TranscriptWord[];
}

interface AnnotationTimeRange {
  start: number;
  end: number;
}

interface SegmentAnnotations {
  clips: Array<{ start: string; end: string; title: string; content: string }>;
  references: Array<{ start: string; end: string; title: string; content: string }>;
  edits: Array<{ start: string; end: string; title: string; content: string }>;
  clipRanges: AnnotationTimeRange[];
  editRanges: AnnotationTimeRange[];
  hasAnnotations: boolean;
}

const EMPTY_SEGMENT_ANNOTATIONS: SegmentAnnotations = {
  clips: [],
  references: [],
  edits: [],
  clipRanges: [],
  editRanges: [],
  hasAnnotations: false,
};

const EMPTY_COMMENTS: any[] = [];

type TimedAnnotation<T extends { start: string; end: string }> = T & {
  startSeconds: number;
  endSeconds: number;
};

type TimedReferenceAnnotation<T extends { start: string }> = T & {
  startSeconds: number;
};

const ReadableTranscriptView: React.FC<ReadableTranscriptViewProps> = ({
  ttmlContent,
  transcript,
  filename,
  transcriptionData,
  speakerLabels,
  audioFile,
  transcriptionChecksum,
  sessionId,
  isModal = false,
  onClose,
  onPlaySegment,
  onPauseSegment,
  showChat = true,
  comments = [],
  isFullscreenTab = false,
  selectedTtmlId = null,
  searchQuery = '',
  searchMatches = [],
  currentMatchIndex = 0,
  onSearchUpdate,
  shouldNavigateToMatch = false,
  onNavigationComplete,
  onCurrentTopicChange,
  showVideoPanel = false,
  typographyConfig = defaultTypographyConfig
}) => {
  const { token } = useAuth();
  const { permissions: sharePermissions, shareToken } = useShareLink();
  const { currentTime, isPlaying, seekTo, play } = useAudio();
  const { isDarkMode } = useDarkMode();
  const { autoScrollEnabled } = useAutoScroll();
  const {
    dragHighlightTime,
    cursorSyncEnabled,
    setCursorSyncEnabled,
    syncRequestId,
    syncRequestTargetTime,
    requestCursorSync,
  } = useAudioUI();
  const isEditedMode = false;
  const hasEditCuts = false;
  const timelineMapper: any = null;
  const toggleEditMode = () => {};

  // Get device performance settings for mobile optimization
  const perfSettings = useDevicePerformance();
  const isPublicShare = !!sharePermissions;
  const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
  const canWritePublicComments = !!sessionId && !!shareToken && !!sharePermissions?.commentsWrite && isOnline;
  const canMutateComments = !!sessionId && ((!!token && !isPublicShare) || canWritePublicComments);

  const [expandedTopics, setExpandedTopics] = useState<Set<number>>(new Set());
  const [playingSegment, setPlayingSegment] = useState<{groupIndex: number, segmentIndex: number} | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [currentPlayingSegment, setCurrentPlayingSegment] = useState<{groupIndex: number, segmentIndex: number} | null>(null);
  const [localCurrentTime, setLocalCurrentTime] = useState<number>(0);
  const [isLocalPlaying, setIsLocalPlaying] = useState(false);
  const segmentRefs = useRef<Record<string, HTMLDivElement>>({});
  const transcriptScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const lastPlayingStateRef = useRef(false);
  const [playingTime, setPlayingTime] = useState<number | undefined>(undefined);
  const lastScrolledSegmentRef = useRef<{groupIndex: number, segmentIndex: number} | null>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef(false);
  const programmaticScrollGuardUntilRef = useRef(0);
  const lastUserScrollIntentAtRef = useRef(0);
  const lastObservedScrollTopRef = useRef(0);
  // Cache last found segment position for O(1) lookup when time moves forward
  const lastFoundPositionRef = useRef<{groupIndex: number, segmentIndex: number} | null>(null);
  const [showAnnotationPopup, setShowAnnotationPopup] = useState<{
    groupIndex: number;
    segmentIndex: number;
    type: 'clips' | 'references' | 'edits';
  } | null>(null);
  const [isOrphanSession, setIsOrphanSession] = useState(false);
  const [, setOrphanError] = useState<string>('');
  const [audioStatus, setAudioStatus] = useState<{
    available: boolean;
    isBlob: boolean;
    url?: string;
    error?: string;
  }>({ available: false, isBlob: false });
  const topicRefs = useRef<Record<number, HTMLDivElement>>({});
  const [visibleTopicGroupIndex, setVisibleTopicGroupIndex] = useState<number | null>(null);
  const [hasTranscriptScrolled, setHasTranscriptScrolled] = useState(false);
  const lastHandledSyncRequestIdRef = useRef(0);

  // Store onSearchUpdate in a ref to avoid it being a dependency in useEffect
  const onSearchUpdateRef = useRef(onSearchUpdate);
  onSearchUpdateRef.current = onSearchUpdate;

  const [editedTtmlContent, setEditedTtmlContent] = useState(ttmlContent);
  
  // Update editedTtmlContent when ttmlContent prop changes
  useEffect(() => {
    setEditedTtmlContent(ttmlContent);
  }, [ttmlContent]);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [supportedModels, setSupportedModels] = useState<string[]>([]);
  const [selectedChatModel, setSelectedChatModel] = useState<string>('');
  const [showCommentPopup, setShowCommentPopup] = useState<{ comments: any[], position: { x: number, y: number }, timestampSeconds?: number } | null>(null);
  const [localComments, setLocalComments] = useState<any[]>([]);
  const [includeTtmlInChat, setIncludeTtmlInChat] = useState(true);
  
  // Search functionality
  const searchMatchRefs = useRef<Record<string, HTMLElement>>({});
  const [localSearchMatches, setLocalSearchMatches] = useState<any[]>([]);
  
  // Helper function to organize comments into threads
  const organizeCommentsIntoThreads = useCallback((flatComments: any[]) => {
    const commentMap = new Map<string, any>();
    const rootComments: any[] = [];
    
    // Create a map of all comments
    flatComments.forEach((comment: any) => {
      const normalizedComment = {
        ...comment,
        userEmail: comment.userEmail || comment.user_email || 'Anonymous',
        userName: comment.userName || comment.user_name || comment.displayName || 'Anonymous',
        timestamp: comment.timestamp || comment.createdAt || comment.created_at || comment.updatedAt || comment.updated_at || new Date().toISOString(),
        timestampSeconds: comment.timestampSeconds ?? comment.timestamp_seconds,
        parentCommentId: comment.parentCommentId ?? comment.parent_comment_id,
        replies: [],
      };
      commentMap.set(normalizedComment.id, normalizedComment);
    });
    
    // Organize into threads
    flatComments.forEach((comment: any) => {
      const mappedComment = commentMap.get(comment.id);
      const parentCommentId = comment.parentCommentId ?? comment.parent_comment_id;
      if (parentCommentId && commentMap.has(parentCommentId)) {
        const parent = commentMap.get(parentCommentId);
        if (parent) {
          parent.replies = parent.replies || [];
          parent.replies.push(mappedComment);
        }
      } else {
        rootComments.push(mappedComment);
      }
    });
    
    return rootComments;
  }, []);
  
  // Load comments from API
  useEffect(() => {
    const loadComments = async () => {
      if (!sessionId || !token || isPublicShare) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          const threadedComments = organizeCommentsIntoThreads(data.comments || []);
          setLocalComments(threadedComments);
        }
      } catch (error) {
        console.error('Failed to load comments:', error);
      }
    };
    
    void loadComments();
  }, [isPublicShare, sessionId, token, organizeCommentsIntoThreads]);
  
  // Update local comments when prop changes
  useEffect(() => {
    const threadedComments = organizeCommentsIntoThreads(comments || []);
    setLocalComments(threadedComments);
  }, [comments, organizeCommentsIntoThreads]);

  const getAnonymousSessionId = useCallback(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    let anonymousSessionId = window.localStorage.getItem('anonymousSessionId');
    if (!anonymousSessionId) {
      anonymousSessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      window.localStorage.setItem('anonymousSessionId', anonymousSessionId);
    }

    return anonymousSessionId;
  }, []);

  const fetchCurrentViewComments = useCallback(async () => {
    if (!sessionId) {
      return null;
    }

    let response: Response | null = null;

    if (isPublicShare && shareToken) {
      response = await fetch(`${API_BASE_URL}/api/public/share/${shareToken}/comments`);
    } else if (token) {
      response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
    }

    if (!response || !response.ok) {
      throw new Error('Failed to refresh comments');
    }

    const data = await response.json();
    const threadedComments = organizeCommentsIntoThreads(data.comments || []);
    setLocalComments(threadedComments);
    return threadedComments;
  }, [isPublicShare, organizeCommentsIntoThreads, sessionId, shareToken, token]);

  // Load supported models for chat (using dynamic OpenRouter models)
  useEffect(() => {
    const loadModels = async () => {
      try {
        // Use the new dynamic OpenRouter models endpoint
        const response = await openRouterService.getOpenRouterModels(token!);
        const modelIds = response.models.map(m => m.id);
        setSupportedModels(modelIds);

        if (modelIds.length > 0) {
          // Check for persisted model first
          const persistedModel = ModelPersistence.getSelectedModel();
          if (persistedModel && modelIds.includes(persistedModel)) {
            setSelectedChatModel(persistedModel);
          } else {
            // Set default to claude-sonnet-4 if available, otherwise use first model
            const defaultModel = modelIds.find(m => m === 'anthropic/claude-sonnet-4') || modelIds[0];
            setSelectedChatModel(defaultModel);
          }
        }
      } catch (err) {
        console.error('Failed to load supported models for chat:', err);
        // Fallback to legacy endpoint
        try {
          const models = await openRouterService.getSupportedModels(token!);
          setSupportedModels(models);
          if (models.length > 0) {
            const persistedModel = ModelPersistence.getSelectedModel();
            if (persistedModel && models.includes(persistedModel)) {
              setSelectedChatModel(persistedModel);
            } else {
              const defaultModel = models.find(m => m === 'anthropic/claude-sonnet-4') || models[0];
              setSelectedChatModel(defaultModel);
            }
          }
        } catch (fallbackErr) {
          console.error('Failed to load models from fallback endpoint:', fallbackErr);
        }
      }
    };

    if (token && isChatOpen) {
      loadModels();
    }
  }, [token, isChatOpen]);

  // Persist model selection when it changes
  useEffect(() => {
    if (selectedChatModel) {
      ModelPersistence.setSelectedModel(selectedChatModel);
    }
  }, [selectedChatModel]);

  const timeToSeconds = useCallback((timeStr: string): number => {
    if (!timeStr) return 0;
    const parts = timeStr.split(/[:.]/);
    if (parts.length < 3 || parts.length > 4) return 0;
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const seconds = parseInt(parts[2], 10) || 0;
    const milliseconds = parts.length === 4 ? parseInt(parts[3], 10) || 0 : 0;
    return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
  }, []);

  // Format seconds to HH:MM:SS or MM:SS
  const formatTimestampLocal = (seconds: number): string => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const pad = (n: number) => String(n).padStart(2, '0');
    if (hours > 0) {
      return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
    }
    return `${pad(minutes)}:${pad(secs)}`;
  };

  const parseTTMLContent = useCallback((ttmlContent: string) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(ttmlContent, 'application/xml');

      // Helper to get elements with namespace fallback (same approach as CentralAudioPlayer)
      const getElements = (parent: Element | Document, tagName: string): Element[] => {
        let elements = parent.querySelectorAll(tagName);
        if (elements.length === 0) {
          // Try with namespace
          const nsElements = parent.getElementsByTagNameNS('*', tagName);
          return Array.from(nsElements);
        }
        return Array.from(elements);
      };

      // Look for summary paragraphs in both region="r_summary" and xml:id="d_summary" formats
      let summaryParagraphs: Element[] = Array.from(xmlDoc.querySelectorAll('div[region="r_summary"] p'));
      console.log('[TTML-PARSE] Found paragraphs with region selector:', summaryParagraphs.length);

      if (summaryParagraphs.length === 0) {
        // Try alternative format with xml:id - iterate through divs and check attribute directly
        // CSS selectors don't work reliably with xml:id namespace attributes
        const allDivs = getElements(xmlDoc, 'div');
        console.log('[TTML-PARSE] Found', allDivs.length, 'div elements, checking for d_summary');

        let summaryDiv: Element | null = null;
        for (const div of allDivs) {
          const xmlId = div.getAttribute('xml:id');
          if (xmlId === 'd_summary') {
            summaryDiv = div;
            break;
          }
        }

        console.log('[TTML-PARSE] Found summary div with getAttribute:', !!summaryDiv);
        if (summaryDiv) {
          summaryParagraphs = getElements(summaryDiv, 'p');
          console.log('[TTML-PARSE] Found paragraphs in summary div:', summaryParagraphs.length);
        }
      }

      const topics: Array<{
        start: string;
        end: string;
        segmentNumber: number;
        rank: number;
        total: number;
        title: string;
        content: string;
        ranking: number;
        positionRank?: number;
        positionTotal?: number;
      }> = [];

      summaryParagraphs.forEach((p, index) => {
        const begin = p.getAttribute('begin') || '';
        const end = p.getAttribute('end') || '';
        let htmlContent = p.innerHTML;
        
        // Decode HTML entities in the content for proper parsing
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        const decodedContent = tempDiv.textContent || '';
        
        console.log(`[TTML-PARSE] Paragraph ${index}:`, {
          begin,
          end,
          htmlContentSample: htmlContent.substring(0, 100),
          decodedContentSample: decodedContent.substring(0, 100)
        });

        // Match both encoded and decoded patterns, including double-encoded entities
        // Supports [1: 20/20] and [1: ?/9] formats (? = unrated position)
        const segmentMatch = htmlContent.match(/\[(\d+):\s*(\d+|\?)\/(\d+)\]\s*(?:&amp;gt;&amp;gt;|&gt;&gt;|>>)\s*\[([^\]]+)\]/) ||
                            decodedContent.match(/\[(\d+):\s*(\d+|\?)\/(\d+)\]\s*>>\s*\[([^\]]+)\]/);

        // Fallback: match [N/Total] >> [Title] format (legacy/broken format without colon)
        const fallbackMatch = !segmentMatch ? (
          htmlContent.match(/\[(\d+)\/(\d+)\]\s*(?:&amp;gt;&amp;gt;|&gt;&gt;|>>)\s*\[([^\]]+)\]/) ||
          decodedContent.match(/\[(\d+)\/(\d+)\]\s*>>\s*\[([^\]]+)\]/)
        ) : null;

        if (segmentMatch || fallbackMatch) {
          const match = (segmentMatch || fallbackMatch)!;
          console.log(`[TTML-PARSE] Found ${segmentMatch ? 'standard' : 'fallback'} match for paragraph ${index}:`, match[0]);
          const segmentNumber = segmentMatch ? parseInt(segmentMatch[1], 10) : (index + 1);
          const positionRank = segmentMatch ? (parseInt(segmentMatch[2], 10) || 0) : parseInt(fallbackMatch![1], 10);
          const positionTotal = segmentMatch ? (parseInt(segmentMatch[3], 10) || 0) : parseInt(fallbackMatch![2], 10);
          const title = segmentMatch ? segmentMatch[4].trim() : fallbackMatch![3].trim();
          
          // Extract the actual score from content, not the position
          let rank = 5;  // default
          let total = 10; // default
          
          // Look for score in decoded content, handle brackets
          const scoreMatch = decodedContent.match(/Score:\s*\[(\d+)\/(\d+)\]/) || 
                           decodedContent.match(/Score:\s*(\d+)\/(\d+)/);
          if (scoreMatch) {
            rank = parseInt(scoreMatch[1], 10);
            total = parseInt(scoreMatch[2], 10);
          }

          topics.push({
            start: begin,
            end: end,
            segmentNumber,
            rank,
            total,
            title: toPlainMetadataText(title),
            content: toPlainMetadataText(htmlContent.replace(/<br\s*\/?>/gi, '\n')),
            ranking: Math.min(10, Math.max(1, Math.ceil((rank / total) * 10))),
            positionRank,
            positionTotal
          });
        }
      });

      topics.sort((a, b) => timeToSeconds(a.start) - timeToSeconds(b.start));

      return topics;
    } catch (error) {
      console.error('Error parsing TTML content:', error);
      return [];
    }
  }, [timeToSeconds]);

  // Helper function to parse reference content into structured fields
  const parseReferenceContent = (content: string): {
    title: string;
    category?: string;
    context?: string;
    url?: string;
    linkText?: string;
  } => {
    const decoded = toPlainMetadataText(content.replace(/<br\s*\/?>/gi, '\n'), true);

    // Split by line breaks (both <br/> tags and actual newlines)
    const lines = decoded
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (lines.length === 0) {
      return { title: toPlainMetadataText(content).substring(0, 50) };
    }

    const headerLine = lines[0];

    // Parse [REF] format: [REF] Name | Link Text | URL
    const refMatch = headerLine.match(/\[REF\]\s*([^|\n]+?)(?:\s*\|\s*([^|\n]+?)\s*\|\s*(https?:\/\/[^\s<>\n]+))?$/);

    let title = headerLine.replace(/\[REF\]\s*/, '').trim();
    let url: string | undefined;
    let linkText: string | undefined;
    let category: string | undefined;
    let context: string | undefined;

    if (refMatch) {
      title = refMatch[1].trim();
      linkText = refMatch[2]?.trim();
      url = refMatch[3]?.trim();
    }

    // Parse additional metadata lines (category, context)
    // Skip lines that start with "Visual:" as those are editor-only suggestions
    const metaLines = lines.slice(1).filter(line => !line.startsWith('Visual:'));
    if (metaLines.length > 0 && metaLines[0]) {
      category = metaLines[0];
    }
    if (metaLines.length > 1 && metaLines[1]) {
      context = metaLines[1];
    }

    return { title, category, context, url, linkText };
  };

  // Helper to parse edit content into structured fields
  // Handles new pipe-delimited format: [EDIT] Title | Priority | Suggestion | Improvement
  // And old format with Suggestion:, Priority:, etc labels
  const parseEditContent = (content: string): {
    title?: string;
    suggestion: string;
    improvement?: string;
    priority?: string;
    note?: string;
  } => {
    let decoded = toPlainMetadataText(content.replace(/<br\s*\/?>/gi, '\n'), true)
      .replace(/\[EDIT\]\s*/g, '')
      .trim();

    // Check for new pipe-delimited format: Title | Priority | Suggestion | Improvement
    if (decoded.includes('|')) {
      const parts = decoded.split('|').map(p => p.trim());
      return {
        title: parts[0] || undefined,
        priority: parts[1] || undefined,
        suggestion: parts[2] || '',
        improvement: parts[3] || undefined
      };
    }

    // Old format: split by <br/> tags OR by known field prefixes
    decoded = decoded
      .replace(/(?=Suggestion:|Improvement:|Priority:|Note:)/g, '\n');

    const lines = decoded
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (lines.length === 0) {
      return { suggestion: decoded.trim() };
    }

    let title: string | undefined;
    let suggestion = '';
    let improvement: string | undefined;
    let priority: string | undefined;
    let note: string | undefined;

    // Parse labeled fields
    for (const line of lines) {
      if (line.startsWith('Suggestion:')) {
        const text = line.replace('Suggestion:', '').trim();
        if (!suggestion) {
          suggestion = text;
        } else {
          // Additional suggestion - treat as improvement if not already set
          if (!improvement) {
            improvement = text;
          }
        }
      } else if (line.startsWith('Improvement:')) {
        improvement = line.replace('Improvement:', '').trim();
      } else if (line.startsWith('Priority:')) {
        priority = line.replace('Priority:', '').trim();
      } else if (line.startsWith('Note:')) {
        note = line.replace('Note:', '').trim();
      } else if (!title && !suggestion) {
        // First unlabeled line is the title (e.g., "The Turing Completeness Debate")
        title = line;
      } else if (!suggestion) {
        suggestion = line;
      }
    }

    // If no labeled suggestion found, use remaining content
    if (!suggestion && title) {
      suggestion = title;
      title = undefined;
    }

    return { title, suggestion, improvement, priority, note };
  };

  // Helper to convert timestamp string to seconds
  const timestampToSeconds = (timestamp: string): number => {
    const parts = timestamp.split(':').map(parseFloat);
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
    return parseFloat(timestamp) || 0;
  };

  const extractTTMLAnnotations = (ttmlContent: string) => {
    const clips: Array<{ start: string; end: string; title: string; content: string }> = [];
    const references: Array<{ start: string; end: string; title: string; content: string }> = [];
    const edits: Array<{ start: string; end: string; title: string; content: string }> = [];

    try {

      // Try to parse as XML first
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(ttmlContent, 'application/xml');
      
      // Look for clips in different possible regions and formats
      let clipElements = xmlDoc.querySelectorAll('div[region="r_clip"] p, div[region="r_clips"] p');
      if (clipElements.length === 0) {
        const clipDiv = xmlDoc.querySelector('div[xml\\:id="d_clip"], div[id="d_clip"]');
        if (clipDiv) {
          clipElements = clipDiv.querySelectorAll('p');
        }
      }
      
      clipElements.forEach(p => {
        const content = p.textContent || p.innerHTML;
        const begin = p.getAttribute('begin');
        const end = p.getAttribute('end');
        
        if (content && content.includes('[CLIP]') && begin && end) {
          const titleMatch = content.match(/\[CLIP\]\s*(.+)$/);
          if (titleMatch) {
            clips.push({
              start: begin,
              end: end,
              title: titleMatch[1].trim(),
              content: content
            });
          }
        }
      });

      // Look for references
      let refElements = xmlDoc.querySelectorAll('div[region="r_references"] p, div[region="r_reference"] p');
      if (refElements.length === 0) {
        const refDiv = xmlDoc.querySelector('div[xml\\:id="d_references"], div[id="d_references"]');
        if (refDiv) {
          refElements = refDiv.querySelectorAll('p');
        }
      }
      
      refElements.forEach(p => {
        const content = p.textContent || p.innerHTML;
        const begin = p.getAttribute('begin');
        const end = p.getAttribute('end');
        
        if (content && content.includes('[REF]') && begin && end) {
          const titleMatch = content.match(/\[REF\]\s*(.+)$/);
          if (titleMatch) {
            references.push({
              start: begin,
              end: end,
              title: titleMatch[1].trim(),
              content: content
            });
          }
        }
      });

      // Look for edits
      let editElements = xmlDoc.querySelectorAll('div[region="r_edit"] p, div[region="r_edits"] p');
      if (editElements.length === 0) {
        const editDiv = xmlDoc.querySelector('div[xml\\:id="d_edit"], div[id="d_edit"]');
        if (editDiv) {
          editElements = editDiv.querySelectorAll('p');
        }
      }
      
      editElements.forEach(p => {
        const content = p.textContent || p.innerHTML;
        const begin = p.getAttribute('begin');
        const end = p.getAttribute('end');
        
        if (content && content.includes('[EDIT]') && begin && end) {
          const titleMatch = content.match(/\[EDIT\]\s*(.+)$/);
          if (titleMatch) {
            edits.push({
              start: begin,
              end: end,
              title: titleMatch[1].trim(),
              content: content
            });
          }
        }
      });

      // Fallback to regex if XML parsing doesn't work
      if (clips.length === 0) {
        const clipMatches = ttmlContent.match(/<p[^>]*begin="([^"]*)"[^>]*end="([^"]*)"[^>]*>[^<]*\[CLIP\][^<]*<\/p>/g);
        if (clipMatches) {
          clipMatches.forEach(match => {
            const beginMatch = match.match(/begin="([^"]*)"/);
            const endMatch = match.match(/end="([^"]*)"/);
            const titleMatch = match.match(/\[CLIP\]\s*([^<]*)/);
            
            if (beginMatch && endMatch && titleMatch) {
              clips.push({
                start: beginMatch[1],
                end: endMatch[1],
                title: titleMatch[1].trim(),
                content: match
              });
            }
          });
        }
      }

      if (references.length === 0) {
        const refMatches = ttmlContent.match(/<p[^>]*begin="([^"]*)"[^>]*end="([^"]*)"[^>]*>[^<]*\[REF\][^<]*<\/p>/g);
        if (refMatches) {
          refMatches.forEach(match => {
            const beginMatch = match.match(/begin="([^"]*)"/);
            const endMatch = match.match(/end="([^"]*)"/);
            const titleMatch = match.match(/\[REF\]\s*([^<]*)/);
            
            if (beginMatch && endMatch && titleMatch) {
              references.push({
                start: beginMatch[1],
                end: endMatch[1],
                title: titleMatch[1].trim(),
                content: match
              });
            }
          });
        }
      }

      if (edits.length === 0) {
        const editMatches = ttmlContent.match(/<p[^>]*begin="([^"]*)"[^>]*end="([^"]*)"[^>]*>[^<]*\[EDIT\][^<]*<\/p>/g);
        if (editMatches) {
          editMatches.forEach(match => {
            const beginMatch = match.match(/begin="([^"]*)"/);
            const endMatch = match.match(/end="([^"]*)"/);
            const titleMatch = match.match(/\[EDIT\]\s*([^<]*)/);
            
            if (beginMatch && endMatch && titleMatch) {
              edits.push({
                start: beginMatch[1],
                end: endMatch[1],
                title: titleMatch[1].trim(),
                content: match
              });
            }
          });
        }
      }

      
    } catch (error) {
      console.error('Error extracting TTML annotations:', error);
    }

    return { clips, references, edits };
  };

  // Parse TTML content - use useMemo to recompute when editedTtmlContent changes
  const topics = useMemo(() => {
    return parseTTMLContent(editedTtmlContent);
  }, [editedTtmlContent, parseTTMLContent]);
  const { clips, references, edits } = useMemo(() => {
    return extractTTMLAnnotations(editedTtmlContent);
  }, [editedTtmlContent]);
  const topicTimeRanges = useMemo(
    () => topics.map((topic) => ({
      ...topic,
      startSeconds: timeToSeconds(topic.start),
      endSeconds: timeToSeconds(topic.end),
    })),
    [timeToSeconds, topics]
  );
  const timedClips = useMemo<Array<TimedAnnotation<typeof clips[number]>>>(
    () => clips.map((clip) => ({
      ...clip,
      startSeconds: timeToSeconds(clip.start),
      endSeconds: timeToSeconds(clip.end),
    })),
    [clips, timeToSeconds]
  );
  const timedReferences = useMemo<Array<TimedReferenceAnnotation<typeof references[number]>>>(
    () => references.map((reference) => ({
      ...reference,
      startSeconds: timeToSeconds(reference.start),
    })),
    [references, timeToSeconds]
  );
  const timedEdits = useMemo<Array<TimedAnnotation<typeof edits[number]>>>(
    () => edits.map((edit) => ({
      ...edit,
      startSeconds: timeToSeconds(edit.start),
      endSeconds: timeToSeconds(edit.end),
    })),
    [edits, timeToSeconds]
  );

  const transcriptWords = useMemo<TranscriptWord[]>(() => {
    if (!transcriptionData?.results) return [];

    if (Array.isArray(transcriptionData.results.utterances) && transcriptionData.results.utterances.length > 0) {
      return transcriptionData.results.utterances.flatMap((utterance, utteranceIndex) =>
        (utterance.words || [])
          .filter((word) => typeof word.start === 'number' && typeof word.end === 'number' && !!word.word?.trim())
          .map((word, wordIndex) => ({
            word: word.punctuated_word || word.word,
            start: word.start,
            end: word.end,
            speaker: typeof word.speaker === 'number'
              ? word.speaker
              : typeof utterance.speaker === 'number'
                ? utterance.speaker
                : 0,
            utteranceIndex,
            wordIndex,
          }))
      );
    }

    const channelWords = transcriptionData.results.channels?.[0]?.alternatives?.[0]?.words || [];
    return channelWords
      .filter((word) => typeof word.start === 'number' && typeof word.end === 'number' && !!word.word?.trim())
      .map((word, wordIndex) => ({
        word: word.punctuated_word || word.word,
        start: word.start,
        end: word.end,
        speaker: typeof word.speaker === 'number' ? word.speaker : 0,
        utteranceIndex: 0,
        wordIndex,
      }));
  }, [transcriptionData]);

  const wordIndex = transcriptWords;


  // Helper function to get annotations for a segment
  const getSegmentAnnotations = useCallback((
    segmentStart: number,
    segmentEnd: number
  ): SegmentAnnotations => {
    // Check for clips (full time span matters)
    const matchingClips = timedClips.filter((clip) => {
      return segmentStart < clip.endSeconds && segmentEnd > clip.startSeconds;
    });

    // Check for references (only start time matters - they're "at this time" annotations)
    const matchingReferences = timedReferences.filter((ref) => {
      // For references, we only care if the segment contains the reference start time
      return segmentStart <= ref.startSeconds && segmentEnd >= ref.startSeconds;
    });

    // Check for edits (full time span matters)
    const matchingEdits = timedEdits.filter((edit) => {
      return segmentStart < edit.endSeconds && segmentEnd > edit.startSeconds;
    });

    const clipRanges = matchingClips.map((clip) => ({
      start: clip.startSeconds,
      end: clip.endSeconds,
    }));

    const editRanges = matchingEdits.map((edit) => ({
      start: edit.startSeconds,
      end: edit.endSeconds,
    }));

    return {
      clips: matchingClips,
      references: matchingReferences,
      edits: matchingEdits,
      clipRanges,
      editRanges,
      hasAnnotations: matchingClips.length > 0 || matchingReferences.length > 0 || matchingEdits.length > 0
    };
  }, [timedClips, timedEdits, timedReferences]);

  const formatTimestamp = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const parseTranscriptSegments = useCallback((): TranscriptSegment[] => {
    if (transcriptWords.length === 0) {
      return [];
    }

    const segments: TranscriptSegment[] = [];

    let currentSegment: {
      speaker: number;
      words: string[];
      timedWords: TranscriptWord[];
      topicIndex: number;
      start: number;
      end: number;
    } | null = null;

    for (const word of transcriptWords) {
      if (word.speaker === undefined || !word.word?.trim()) continue;

      const wordText = word.word;
      const wordMidpoint = (word.start + word.end) / 2;
      const wordTopicIndex = topicTimeRanges.findIndex((topic) => {
        return wordMidpoint >= topic.startSeconds && wordMidpoint <= topic.endSeconds;
      });
      const shouldStartNewSegment = !currentSegment
        || currentSegment.speaker !== word.speaker
        || currentSegment.topicIndex !== wordTopicIndex;

      if (currentSegment && !shouldStartNewSegment) {
        currentSegment.words.push(wordText);
        currentSegment.timedWords.push(word);
        currentSegment.end = word.end;
      } else {
        if (currentSegment && currentSegment.words.length > 0) {
          const speakerName = speakerLabels?.[currentSegment.speaker] || `Speaker ${currentSegment.speaker}`;
          const timestamp = formatTimestamp(currentSegment.start);
          segments.push({
            speaker: speakerName,
            text: currentSegment.words.join(' '),
            timestamp,
            timestampSeconds: currentSegment.start,
            endTimestampSeconds: currentSegment.end,
            words: currentSegment.timedWords
          });
        }

        currentSegment = {
          speaker: word.speaker,
          words: [wordText],
          timedWords: [word],
          topicIndex: wordTopicIndex,
          start: word.start,
          end: word.end
        };
      }
    }

    if (currentSegment && currentSegment.words.length > 0) {
      const speakerName = speakerLabels?.[currentSegment.speaker] || `Speaker ${currentSegment.speaker}`;
      const timestamp = formatTimestamp(currentSegment.start);
      segments.push({
        speaker: speakerName,
        text: currentSegment.words.join(' '),
        timestamp,
        timestampSeconds: currentSegment.start,
        endTimestampSeconds: currentSegment.end,
        words: currentSegment.timedWords
      });
    }
    return segments;
  }, [formatTimestamp, speakerLabels, topicTimeRanges, transcriptWords]);

  // Find the current word being spoken.
  const currentWordIndex = useMemo(() => {
    const playing = isPlaying || isLocalPlaying;
    let time = isLocalPlaying ? localCurrentTime : currentTime;

    if (!playing || !time || wordIndex.length === 0) return -1;

    // In edit mode, the currentTime is in edited timeline but wordIndex has original times
    // Convert edited time back to original time for word lookup
    if (isEditedMode && timelineMapper) {
      const originalTime = timelineMapper.editedToOriginal(time);
      if (originalTime === null) {
        // Time is in a cut region (shouldn't happen with edited video, but handle gracefully)
        return -1;
      }
      time = originalTime;
    }

    // Binary search for efficiency
    let left = 0;
    let right = wordIndex.length - 1;
    let foundIndex = -1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const word = wordIndex[mid];

      if (time >= word.start && time <= word.end) {
        foundIndex = mid;
        break;
      } else if (time < word.start) {
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }

    // If not found exactly, find the closest word
    if (foundIndex === -1) {
      if (left <= 0) {
        foundIndex = -1;
      } else if (left >= wordIndex.length) {
        foundIndex = wordIndex.length - 1;
      } else {
        foundIndex = left - 1;
      }
    }

    return foundIndex;
  }, [currentTime, isPlaying, wordIndex, isLocalPlaying, localCurrentTime, isEditedMode, timelineMapper]);

  // Helper function to format duration in human-readable format
  function formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }

  // Helper function to get topic duration and time range
  const formatTopicClock = (seconds: number) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const secs = Math.floor(safeSeconds % 60);
    return hours > 0
      ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      : `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTopicTimeInfo = (topic: { start: string; end: string }) => {
    const startSeconds = timeToSeconds(topic.start);
    const endSeconds = timeToSeconds(topic.end);
    const durationSeconds = endSeconds - startSeconds;
    
    return {
      startTime: formatTopicClock(startSeconds),
      endTime: formatTopicClock(endSeconds),
      duration: formatDuration(durationSeconds),
      startSeconds,
      endSeconds
    };
  };

  const getTopicDescription = useCallback((topic: { content: string }) => {
    return extractTopicDescription(topic.content);
  }, []);

  const playAudioSegment = async (groupIndex: number, segmentIndex: number, startTime: number, endTime: number) => {
    // If we have a custom play handler (for tab mode), use it
    if (onPlaySegment) {
      // If clicking the same segment that's playing, stop it
      if (playingSegment?.groupIndex === groupIndex && playingSegment?.segmentIndex === segmentIndex) {
        setPlayingSegment(null);
        if (onPauseSegment) {
          onPauseSegment();
        }
        return;
      }

      onPlaySegment(startTime, endTime);
      setPlayingSegment({ groupIndex, segmentIndex });

      // Clear playing state after segment duration
      const duration = (endTime - startTime) * 1000;
      setTimeout(() => {
        setPlayingSegment(null);
      }, duration);

      return;
    }

    // Otherwise, use internal audio playback (for modal mode)
    try {
      // Stop any currently playing audio
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }

      if (playingSegment?.groupIndex === groupIndex && playingSegment?.segmentIndex === segmentIndex) {
        // Stop playing if clicking the same segment
        setPlayingSegment(null);
        setAudioElement(null);
        setIsLocalPlaying(false);
        setLocalCurrentTime(0);
        return;
      }

      // Check if we have an audio file
      if (!audioFile && !transcriptionChecksum) {
        return;
      }

      // Use direct audio URL from backend for both uploaded files and cached audio
      let audioUrl: string;
      
      if (transcriptionChecksum) {
        // Use direct audio URL (no auth needed - backend handles it)
        console.log(`[AUDIO] Using direct audio URL for checksum: ${transcriptionChecksum}`);
        audioUrl = `/api/audio/${transcriptionChecksum}/original`;
        
        setAudioStatus({ 
          available: true, 
          isBlob: false, 
          url: audioUrl 
        });
      } else {
        console.log('[AUDIO] No transcription checksum available for audio playback');
        setAudioStatus({ 
          available: false, 
          isBlob: false, 
          error: 'No audio source available' 
        });
        return;
      }

      const audio = new Audio(audioUrl);
      audio.currentTime = startTime;
      
      setPlayingSegment({ groupIndex, segmentIndex });
      setAudioElement(audio);
      setIsLocalPlaying(true); // Set immediately when creating audio

      audio.addEventListener('loadeddata', () => {
        audio.play();
        setIsLocalPlaying(true);
      });

      audio.addEventListener('error', async (e) => {
        console.error('[AUDIO] Error playing audio', e);
        
        // Check if it's a 404 error that might indicate orphan session
        if (transcriptionChecksum && !audioFile) {
          try {
            const response = await fetch(`${API_BASE_URL}/api/audio/${transcriptionChecksum}/original`, {
              method: 'HEAD',
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            if (!response.ok && response.status === 404) {
              // Check if it's an orphan session
              try {
                const errorResponse = await fetch(`${API_BASE_URL}/api/audio/${transcriptionChecksum}/original`, {
                  headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
                const errorData = await errorResponse.json().catch(() => ({}));
                if (errorData.isOrphan) {
                  setIsOrphanSession(true);
                  setOrphanError('Audio file is missing from this session. Please upload the original audio file to enable playback.');
                  setAudioStatus(prev => ({
                    ...prev,
                    error: 'Audio file missing - orphan session detected'
                  }));
                  setPlayingSegment(null);
                  setAudioElement(null);
                  return;
                }
              } catch (e) {
                console.log('[AUDIO] Could not check orphan status');
              }
            }
          } catch (e) {
            console.log('[AUDIO] Could not test audio URL');
          }
        }
        
        setAudioStatus(prev => ({
          ...prev,
          error: 'Audio playback failed'
        }));
        setPlayingSegment(null);
        setAudioElement(null);
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
      });

      audio.addEventListener('ended', () => {
        setPlayingSegment(null);
        setAudioElement(null);
        setIsLocalPlaying(false);
        setLocalCurrentTime(0);
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
      });

      // Stop at end time
      audio.addEventListener('timeupdate', () => {
        setLocalCurrentTime(audio.currentTime);
        if (audio.currentTime >= endTime) {
          audio.pause();
          setPlayingSegment(null);
          setAudioElement(null);
          setIsLocalPlaying(false);
          setLocalCurrentTime(0);
          URL.revokeObjectURL(audioUrl);
        }
      });

    } catch (error) {
      console.error('Error playing audio segment:', error);
      setPlayingSegment(null);
      setAudioElement(null);
    }
  };


  const transcriptSegments = useMemo(() => parseTranscriptSegments(), [parseTranscriptSegments]);

  const groupSegmentsByTopics = useCallback(() => {
    const groups: Array<{
      topic: typeof topics[0];
      segments: typeof transcriptSegments;
    }> = [];

    if (topics.length === 0) {
      groups.push({
        topic: {
          start: '00:00:00',
          end: '99:99:99',
          rank: 1,
          total: 1,
          segmentNumber: 1,
          title: 'Full Transcript',
          content: '',
          ranking: 5
        },
        segments: transcriptSegments
      });
      return groups;
    }

    // Create groups with empty segments arrays
    for (const topic of topics) {
      groups.push({
        topic,
        segments: []
      });
    }

    // For each segment, find the most appropriate topic
    for (const segment of transcriptSegments) {
      if (!segment.timestamp) continue;

      const segmentStart = segment.timestampSeconds;
      const segmentEnd = segment.endTimestampSeconds;
      const segmentMidpoint = (segmentStart + segmentEnd) / 2;


      let bestTopicIndex = -1;
      let bestScore = -1;

      // Try different assignment strategies
      for (let i = 0; i < topicTimeRanges.length; i++) {
        const topic = topicTimeRanges[i];
        const topicStart = topic.startSeconds;
        const topicEnd = topic.endSeconds;
        
        // Strategy 1: Direct overlap
        const overlapStart = Math.max(segmentStart, topicStart);
        const overlapEnd = Math.min(segmentEnd, topicEnd);
        const overlapDuration = Math.max(0, overlapEnd - overlapStart);
        const segmentDuration = segmentEnd - segmentStart;
        const overlapRatio = segmentDuration > 0 ? overlapDuration / segmentDuration : 0;

        // Strategy 2: Proximity to topic boundaries (even if no direct overlap)
        const distanceToTopicStart = Math.abs(segmentMidpoint - topicStart);
        const distanceToTopicEnd = Math.abs(segmentMidpoint - topicEnd);
        const minDistanceToTopic = Math.min(distanceToTopicStart, distanceToTopicEnd);
        
        // Strategy 3: Check if segment is within topic boundaries with some tolerance
        const tolerance = 30; // 30 seconds tolerance
        const withinBoundaries = segmentMidpoint >= (topicStart - tolerance) && segmentMidpoint <= (topicEnd + tolerance);

        // Calculate a composite score
        let score = 0;
        
        if (overlapRatio > 0) {
          // Direct overlap gets highest priority
          score = 1000 + overlapRatio * 100;
        } else if (withinBoundaries) {
          // Within boundaries (with tolerance) gets medium priority
          score = 500 - minDistanceToTopic;
        } else {
          // Close proximity gets lower priority
          score = 100 - minDistanceToTopic;
        }

        if (score > bestScore) {
          bestScore = score;
          bestTopicIndex = i;
        }
      }

      // Always assign to the best scoring topic
      if (bestTopicIndex >= 0) {
        groups[bestTopicIndex].segments.push(segment);
      } else {
        // Fallback: assign to first topic
        groups[0].segments.push(segment);
      }
    }


    return groups;
  }, [topicTimeRanges, topics, transcriptSegments]);

  const { showGradient, mode: topicVisualizationMode, retentionMode, retentionData } = useTopicVisualization();

  // Helper to get topic score based on current visualization mode
  // Returns { score, maxScore, ratio } for the selected ranking category
  const getTopicScore = useCallback((topic: typeof topics[0]): { score: number; maxScore: number; ratio: number } => {
    let score = topic.rank || 0;
    let maxScore = topic.total || 10;

    if (topicVisualizationMode === 'overall') {
      score = topic.rank || 0;
      maxScore = topic.total || 10;
    } else if (topicVisualizationMode === 'substance') {
      // Substance: S: X/3
      const match = topic.content.match(/S:\s*(\d+)\/(\d+)/);
      if (match) {
        score = parseInt(match[1]);
        maxScore = parseInt(match[2]);
      }
    } else if (topicVisualizationMode === 'resonance') {
      // Resonance: R: X/2
      const match = topic.content.match(/R:\s*(\d+)\/(\d+)/);
      if (match) {
        score = parseInt(match[1]);
        maxScore = parseInt(match[2]);
      }
    } else if (topicVisualizationMode === 'clarity') {
      // Clarity: C: X/2
      const match = topic.content.match(/C:\s*(\d+)\/(\d+)/);
      if (match) {
        score = parseInt(match[1]);
        maxScore = parseInt(match[2]);
      }
    } else if (topicVisualizationMode === 'production') {
      // Production: P: X/2
      const match = topic.content.match(/P:\s*(\d+)\/(\d+)/);
      if (match) {
        score = parseInt(match[1]);
        maxScore = parseInt(match[2]);
      }
    } else if (topicVisualizationMode === 'creative_risk') {
      // Creative Risk: CR: X/1
      const match = topic.content.match(/CR:\s*(\d+)\/(\d+)/);
      if (match) {
        score = parseInt(match[1]);
        maxScore = parseInt(match[2]);
      }
    }

    const ratio = maxScore > 0 ? score / maxScore : 0;
    return { score, maxScore, ratio };
  }, [topicVisualizationMode]);

  const fallbackRetentionDuration = useMemo(() => {
    const metadataDuration = Number(transcriptionData?.metadata?.duration);
    if (Number.isFinite(metadataDuration) && metadataDuration > 0) {
      return metadataDuration;
    }

    return topics.reduce((max, topic) => {
      const topicEnd = timeToSeconds(topic.end);
      return topicEnd > max ? topicEnd : max;
    }, 0);
  }, [transcriptionData, timeToSeconds, topics]);

  // Helper to get retention value at a specific time for topic coloring
  const getTopicRetentionRatio = useCallback((topic: typeof topics[0]): number | null => {
    if (!retentionData?.retentionData?.length) return null;
    if (retentionMode === 'off' || retentionMode === 'drops') return null;

    // Get topic's midpoint time for retention lookup
    const topicStartTime = timeToSeconds(topic.start);
    const topicEndTime = timeToSeconds(topic.end);
    const midpointTime = (topicStartTime + topicEndTime) / 2;

    const videoDuration = retentionData.videoDurationSeconds || fallbackRetentionDuration;
    if (videoDuration === 0) return null;

    // Find the closest retention data point to the topic's midpoint
    let closest = retentionData.retentionData[0];
    let minDiff = Infinity;

    for (const point of retentionData.retentionData) {
      const timestamp = point.timestampSeconds !== undefined
        ? point.timestampSeconds
        : point.ratio * videoDuration;
      const diff = Math.abs(timestamp - midpointTime);
      if (diff < minDiff) {
        minDiff = diff;
        closest = point;
      }
    }

    // Get retention value based on mode - use absolute value, not relative percentile
    switch (retentionMode) {
      case 'absolute':
        return closest.absoluteRetention / 100;
      case 'adjusted':
        return closest.relativeRetention * Math.sqrt(closest.absoluteRetention / 100);
      case 'relative':
      default:
        return closest.relativeRetention;
    }
  }, [retentionData, retentionMode, timeToSeconds, fallbackRetentionDuration]);

  // Normalize a raw retention value to a 0-1 ratio suitable for color thresholds.
  // Raw retention values cluster in narrow ranges depending on mode (e.g., relative
  // clusters around 0.5, adjusted around 0.1-0.3), so we map deviation from the
  // mode's baseline onto the full 0-1 gradient to get meaningful color variation.
  const normalizeRetentionForColor = useCallback((rawValue: number): number => {
    const clampVal = (v: number) => Math.max(0, Math.min(1, v));
    if (!retentionData?.retentionData?.length) return clampVal(rawValue);

    const points = retentionData.retentionData;

    if (retentionMode === 'relative') {
      const baseline = 0.5;
      const allValues = points.map((p: any) => p.relativeRetention ?? 0.5);
      const minVal = Math.min(...allValues);
      const maxVal = Math.max(...allValues);

      if (rawValue >= baseline) {
        const aboveRange = maxVal - baseline;
        if (aboveRange > 0.001) return 0.4 + 0.6 * ((rawValue - baseline) / aboveRange);
        return 0.5;
      } else {
        const belowRange = baseline - minVal;
        if (belowRange > 0.001) return 0.4 * ((rawValue - minVal) / belowRange);
        return 0.3;
      }
    } else if (retentionMode === 'adjusted') {
      const avgAbs = points.reduce((sum: number, p: any) => sum + (p.absoluteRetention ?? 50) / 100, 0) / points.length;
      const baseline = 0.5 * Math.sqrt(avgAbs);
      const allValues = points.map((p: any) => {
        const rel = p.relativeRetention ?? 0.5;
        const abs = (p.absoluteRetention ?? 50) / 100;
        return rel * Math.sqrt(abs);
      });
      const minVal = Math.min(...allValues);
      const maxVal = Math.max(...allValues);

      if (rawValue >= baseline) {
        const aboveRange = maxVal - baseline;
        if (aboveRange > 0.001) return 0.4 + 0.6 * ((rawValue - baseline) / aboveRange);
        return 0.5;
      } else {
        const belowRange = baseline - minVal;
        if (belowRange > 0.001) return 0.4 * ((rawValue - minVal) / belowRange);
        return 0.3;
      }
    } else {
      const allValues = points.map((p: any) => (p.absoluteRetention ?? 50) / 100);
      const minVal = Math.min(...allValues);
      const maxVal = Math.max(...allValues);
      const range = maxVal - minVal;
      if (range > 0.001) return clampVal((rawValue - minVal) / range);
      return 0.5;
    }
  }, [retentionMode, retentionData]);

  const getRankingColor = useCallback((topic: typeof topics[0], isSticky: boolean = false): string => {
    // If retention mode is active and we have data, use retention-based coloring
    if (retentionMode !== 'off' && retentionMode !== 'drops' && retentionData?.retentionData?.length) {
      const retentionRatio = getTopicRetentionRatio(topic);
      if (retentionRatio !== null) {
        return getRetentionColor(normalizeRetentionForColor(retentionRatio), showGradient, isSticky);
      }
    }

    // Use absolute score ratio from the selected ranking category
    const { ratio } = getTopicScore(topic);

    let colorClass = '';

    // Use the same color logic as CentralAudioPlayer but with CSS classes
    if (showGradient) {
      // Gradient from red through warm highlights into MLST navy
      if (ratio >= 0.8) {
        colorClass = isSticky
          ? 'bg-[#e8edf2] dark:bg-[#232E3F]/70 border-[#b7c4d3] dark:border-[#41526a] text-[#232E3F] dark:text-[#F4F4F4]'
          : 'bg-[#e8edf2] dark:bg-[#232E3F]/40 border-[#b7c4d3] dark:border-[#41526a]/60 text-[#232E3F] dark:text-[#F4F4F4]';
      } else if (ratio >= 0.6) {
        colorClass = isSticky
          ? 'bg-[#fff3d6] dark:bg-[#4b3913]/55 border-[#f0cf85] dark:border-[#8a6115] text-[#7a560c] dark:text-[#ffe09b]'
          : 'bg-[#fff3d6] dark:bg-[#4b3913]/35 border-[#f0cf85] dark:border-[#8a6115]/70 text-[#7a560c] dark:text-[#ffe09b]';
      } else if (ratio >= 0.4) {
        colorClass = isSticky
          ? 'bg-yellow-100 dark:bg-yellow-900 border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200'
          : 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700/50 text-yellow-800 dark:text-yellow-200';
      } else if (ratio >= 0.2) {
        colorClass = isSticky
          ? 'bg-orange-100 dark:bg-orange-900 border-orange-300 dark:border-orange-700 text-orange-800 dark:text-orange-200'
          : 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700/50 text-orange-800 dark:text-orange-200';
      } else {
        colorClass = isSticky
          ? 'bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-700 text-red-800 dark:text-red-200'
          : 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700/50 text-red-800 dark:text-red-200';
      }
    } else {
      // Simple three-color scheme using MLST navy / yellow / red
      if (ratio >= 0.67) {
        colorClass = isSticky
          ? 'bg-[#e8edf2] dark:bg-[#232E3F]/70 border-[#b7c4d3] dark:border-[#41526a] text-[#232E3F] dark:text-[#F4F4F4]'
          : 'bg-[#e8edf2] dark:bg-[#232E3F]/40 border-[#b7c4d3] dark:border-[#41526a]/60 text-[#232E3F] dark:text-[#F4F4F4]';
      } else if (ratio >= 0.33) {
        colorClass = isSticky
          ? 'bg-yellow-100 dark:bg-yellow-900 border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200'
          : 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700/50 text-yellow-800 dark:text-yellow-200';
      } else {
        colorClass = isSticky
          ? 'bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-700 text-red-800 dark:text-red-200'
          : 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700/50 text-red-800 dark:text-red-200';
      }
    }

    return colorClass;
  }, [showGradient, retentionMode, retentionData, getTopicRetentionRatio, normalizeRetentionForColor, getTopicScore]);

  // Key to force re-render of topic list when visualization mode changes
  const visualizationKey = `${topicVisualizationMode}-${retentionMode}-${showGradient}`;

  const getSpeakerColor = useCallback((speaker: string) => {
    // Get the actual speaker ID from speakerLabels
    let speakerId: number | undefined;
    
    if (speakerLabels) {
      // Find the speaker ID by looking up the speaker name in speakerLabels
      for (const [id, label] of Object.entries(speakerLabels)) {
        if (label === speaker) {
          speakerId = parseInt(id);
          break;
        }
      }
    }
    
    // If we couldn't find the speaker ID from labels, extract from "Speaker X" format
    if (speakerId === undefined) {
      const match = speaker.match(/Speaker (\d+)/);
      if (match) {
        speakerId = parseInt(match[1]);
      } else {
        // For custom speaker names without a mapping, use hash
        let hash = 0;
        for (let i = 0; i < speaker.length; i++) {
          hash = ((hash << 5) - hash) + speaker.charCodeAt(i);
          hash = hash & hash; // Convert to 32bit integer
        }
        speakerId = Math.abs(hash);
      }
    }
    
    return getSpeakerColorValue(speakerId, isDarkMode);
  }, [isDarkMode, speakerLabels]);

  const withAlpha = useCallback((color: string, alpha: number) => {
    const hexMatch = color.match(/^#([0-9a-f]{6})$/i);
    if (hexMatch) {
      const value = hexMatch[1];
      const r = parseInt(value.slice(0, 2), 16);
      const g = parseInt(value.slice(2, 4), 16);
      const b = parseInt(value.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!match) {
      return color;
    }
    const [, r, g, b] = match;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }, []);

  const toggleTopic = useCallback((index: number) => {
    setExpandedTopics(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(index)) {
        newExpanded.delete(index);
      } else {
        newExpanded.add(index);
      }
      return newExpanded;
    });
  }, []);

  const groupedSegments = useMemo(() => groupSegmentsByTopics(), [groupSegmentsByTopics]);
  const shouldFollowTranscript = autoScrollEnabled && cursorSyncEnabled;

  // Edit-aware version of groupedSegments - filters out segments in cut regions when in edited mode
  const editAwareGroupedSegments = useMemo(() => {
    if (!isEditedMode || !timelineMapper) {
      return groupedSegments;
    }

    return groupedSegments
      .map(group => {
        // Filter segments that are not fully in cut regions
        const filteredSegments = group.segments.filter(segment => {
          const start = segment.timestampSeconds || 0;
          const end = segment.endTimestampSeconds || start;
          // Keep segment if it's not fully in a cut region
          return !timelineMapper.isFullyInCutRegion(start, end);
        });

        // Map timestamps to edited timeline for remaining segments
        const mappedSegments = filteredSegments.map(segment => {
          const start = segment.timestampSeconds || 0;
          const end = segment.endTimestampSeconds || start;
          let editedStart = timelineMapper.originalToEdited(start);
          const editedEnd = timelineMapper.originalToEdited(end);

          // If start is in a cut region but end is not, use the cut's end time as the effective start
          if (editedStart === null && editedEnd !== null) {
            const cut = timelineMapper.getCutAt(start);
            if (cut) {
              editedStart = timelineMapper.originalToEdited(cut.endTime);
            }
          }

          // Format edited timestamp for display
          const editedStartSeconds = editedStart ?? start;
          const hours = Math.floor(editedStartSeconds / 3600);
          const minutes = Math.floor((editedStartSeconds % 3600) / 60);
          const secs = Math.floor(editedStartSeconds % 60);
          const editedTimestampString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

          return {
            ...segment,
            // Store original timestamps for word highlighting
            _originalTimestampSeconds: start,
            _originalEndTimestampSeconds: end,
            _originalTimestamp: segment.timestamp,
            // Use edited timestamps for display
            timestamp: editedTimestampString,
            timestampSeconds: editedStartSeconds,
            endTimestampSeconds: editedEnd ?? end,
            // Flag if this segment overlaps with a cut region (partial overlap)
            _overlapsWithCut: timelineMapper.isInCutRegion(start, end)
          };
        });

        // Remap topic times to edited timeline
        const topicStartSeconds = timeToSeconds(group.topic.start);
        const topicEndSeconds = timeToSeconds(group.topic.end);
        let editedTopicStart = timelineMapper.originalToEdited(topicStartSeconds);
        const editedTopicEnd = timelineMapper.originalToEdited(topicEndSeconds);

        // If topic start is in a cut region but end is not, use the cut's end time
        if (editedTopicStart === null && editedTopicEnd !== null) {
          const cut = timelineMapper.getCutAt(topicStartSeconds);
          if (cut) {
            editedTopicStart = timelineMapper.originalToEdited(cut.endTime);
          }
        }

        // Format remapped times as HH:MM:SS.mmm strings
        const formatTimeString = (seconds: number): string => {
          const h = Math.floor(seconds / 3600);
          const m = Math.floor((seconds % 3600) / 60);
          const s = Math.floor(seconds % 60);
          const ms = Math.floor((seconds % 1) * 1000);
          return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
        };

        return {
          ...group,
          segments: mappedSegments,
          // Remap topic times
          topic: {
            ...group.topic,
            _originalStart: group.topic.start,
            _originalEnd: group.topic.end,
            start: editedTopicStart !== null ? formatTimeString(editedTopicStart) : group.topic.start,
            end: editedTopicEnd !== null ? formatTimeString(editedTopicEnd) : group.topic.end
          },
          // Flag if topic overlaps with any cuts
          _hasOverlappingCuts: timelineMapper.isInCutRegion(topicStartSeconds, topicEndSeconds)
        };
      })
      // Remove groups with no segments left after filtering
      .filter(group => group.segments.length > 0);
  }, [groupedSegments, isEditedMode, timeToSeconds, timelineMapper]);

  // Use edit-aware segments for rendering
  const displaySegments = editAwareGroupedSegments;
  const wordSegmentPositions = useMemo(() => {
    const positions = new Map<string, { groupIndex: number; segmentIndex: number }>();
    displaySegments.forEach((group, groupIndex) => {
      group.segments.forEach((segment, segmentIndex) => {
        (segment.words || []).forEach((word) => {
          positions.set(buildTranscriptWordKey(word), { groupIndex, segmentIndex });
        });
      });
    });
    return positions;
  }, [displaySegments]);
  const updateVisibleTopicGroupIndex = useCallback(() => {
    const scrollContainer = transcriptScrollContainerRef.current;
    if (!scrollContainer || displaySegments.length === 0) {
      setVisibleTopicGroupIndex(null);
      return;
    }

    const stickyOffset = perfSettings.isMobile ? 72 : 48;
    const scrollTop = scrollContainer.scrollTop;
    let nextIndex = 0;

    for (let index = 0; index < displaySegments.length; index += 1) {
      const topicElement = topicRefs.current[index];
      if (!topicElement) {
        continue;
      }

      if (topicElement.offsetTop - scrollTop <= stickyOffset) {
        nextIndex = index;
      } else {
        break;
      }
    }

    setVisibleTopicGroupIndex((previous) => (previous === nextIndex ? previous : nextIndex));
  }, [displaySegments, perfSettings.isMobile]);

  const currentTopicGroupIndex = useMemo(() => {
    if (shouldFollowTranscript && currentPlayingSegment) {
      return currentPlayingSegment.groupIndex;
    }

    if (visibleTopicGroupIndex !== null) {
      return visibleTopicGroupIndex;
    }

    const activeTime = dragHighlightTime ?? (isLocalPlaying ? localCurrentTime : currentTime);
    if (!Number.isFinite(activeTime) || displaySegments.length === 0) {
      return null;
    }

    for (let index = 0; index < displaySegments.length; index += 1) {
      const topic = displaySegments[index].topic;
      const topicStart = timeToSeconds(topic.start);
      const topicEnd = timeToSeconds(topic.end);
      if (activeTime >= topicStart && activeTime <= topicEnd) {
        return index;
      }
    }

    return null;
  }, [
    currentPlayingSegment,
    currentTime,
    displaySegments,
    dragHighlightTime,
    isLocalPlaying,
    localCurrentTime,
    shouldFollowTranscript,
    timeToSeconds,
    visibleTopicGroupIndex,
  ]);
  const currentTopicGroup = currentTopicGroupIndex !== null ? displaySegments[currentTopicGroupIndex] : null;
  const currentTopicTitle = currentTopicGroup
    ? toPlainMetadataText(currentTopicGroup.topic.title) || 'Untitled topic'
    : null;
  const currentTopicDescription = currentTopicGroup
    ? getTopicDescription(currentTopicGroup.topic)
    : null;
  const shouldShowStickyCurrentTopic = Boolean(currentTopicGroup && hasTranscriptScrolled);
  const stickyCurrentTopicGroup = shouldShowStickyCurrentTopic ? currentTopicGroup : null;

  const getTranscriptScrollTarget = useCallback((groupIndex: number, segmentIndex: number) => {
    const segmentKey = `${groupIndex}-${segmentIndex}`;
    const segmentElement = segmentRefs.current[segmentKey];
    if (!segmentElement) {
      return null;
    }

    const activeWordElement = segmentElement.querySelector<HTMLElement>('[data-current-word="true"]');
    return activeWordElement || segmentElement;
  }, []);

  const getTranscriptVisibleBounds = useCallback((scrollContainer: HTMLDivElement) => {
    const containerRect = scrollContainer.getBoundingClientRect();
    let visibleTop = containerRect.top;
    let visibleBottom = containerRect.bottom;

    if (perfSettings.isCompactTouchLayout && showVideoPanel) {
      const videoPanel = document.querySelector<HTMLElement>('[data-session-video-panel="true"]');
      if (videoPanel) {
        const videoRect = videoPanel.getBoundingClientRect();
        const horizontalOverlap = Math.min(containerRect.right, videoRect.right) - Math.max(containerRect.left, videoRect.left);
        if (horizontalOverlap > 48) {
          visibleTop = Math.max(visibleTop, videoRect.bottom + 8);
        }
      }
    }

    const stickyTopic = scrollContainer.querySelector<HTMLElement>('[data-sticky-current-topic="true"]');
    if (stickyTopic) {
      visibleTop = Math.max(visibleTop, stickyTopic.getBoundingClientRect().bottom + 6);
    }

    if (visibleBottom - visibleTop < 80) {
      return {
        top: containerRect.top,
        bottom: containerRect.bottom,
      };
    }

    return {
      top: visibleTop,
      bottom: visibleBottom,
    };
  }, [perfSettings.isCompactTouchLayout, showVideoPanel]);

  const scrollTranscriptTarget = useCallback((groupIndex: number, segmentIndex: number) => {
    const scrollContainer = transcriptScrollContainerRef.current;
    const targetElement = getTranscriptScrollTarget(groupIndex, segmentIndex);
    if (!scrollContainer || !targetElement) {
      return false;
    }

    const { top: visibleTop, bottom: visibleBottom } = getTranscriptVisibleBounds(scrollContainer);
    const targetRect = targetElement.getBoundingClientRect();
    const safePadding = perfSettings.isCompactTouchLayout ? 18 : 28;
    const usableTop = visibleTop + safePadding;
    const usableBottom = Math.max(usableTop + 1, visibleBottom - safePadding);
    const desiredCenterY = usableTop + ((usableBottom - usableTop) * (perfSettings.isCompactTouchLayout ? 0.42 : 0.46));
    const nextTop = scrollContainer.scrollTop
      + (targetRect.top - visibleTop)
      + (targetRect.height / 2)
      - (desiredCenterY - visibleTop);
    const maxTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    const scrollGuardDurationMs = perfSettings.isCompactTouchLayout
      ? 2600
      : perfSettings.isMobile
        ? 2200
        : (perfSettings.useSmoothScroll ? 1400 : 300);

    programmaticScrollGuardUntilRef.current = Date.now() + scrollGuardDurationMs;

    scrollContainer.scrollTo({
      top: Math.max(0, Math.min(nextTop, maxTop)),
      behavior: perfSettings.useSmoothScroll ? 'smooth' : 'auto',
    });

    return true;
  }, [getTranscriptScrollTarget, getTranscriptVisibleBounds, perfSettings.isCompactTouchLayout, perfSettings.isMobile, perfSettings.useSmoothScroll]);
  const shouldRecenterTranscriptTarget = useCallback((groupIndex: number, segmentIndex: number) => {
    const scrollContainer = transcriptScrollContainerRef.current;
    const targetElement = getTranscriptScrollTarget(groupIndex, segmentIndex);
    if (!scrollContainer || !targetElement) {
      return false;
    }

    const { top: visibleTop, bottom: visibleBottom } = getTranscriptVisibleBounds(scrollContainer);
    const targetRect = targetElement.getBoundingClientRect();
    const safePadding = perfSettings.isCompactTouchLayout ? 18 : 28;
    const usableTop = visibleTop + safePadding;
    const usableBottom = Math.max(usableTop + 1, visibleBottom - safePadding);
    const usableHeight = usableBottom - usableTop;
    const targetCenterY = targetRect.top + (targetRect.height / 2);
    const upperFollowBand = usableTop + (usableHeight * (perfSettings.isCompactTouchLayout ? 0.24 : 0.28));
    const lowerFollowBand = usableTop + (usableHeight * (perfSettings.isCompactTouchLayout ? 0.72 : 0.68));

    return targetCenterY < upperFollowBand || targetCenterY > lowerFollowBand;
  }, [getTranscriptScrollTarget, getTranscriptVisibleBounds, perfSettings.isCompactTouchLayout]);

  useEffect(() => {
    onCurrentTopicChange?.(currentTopicTitle);
  }, [currentTopicTitle, onCurrentTopicChange]);

  useEffect(() => () => {
    onCurrentTopicChange?.(null);
  }, [onCurrentTopicChange]);

  // Precompute annotation matches so playback ticks do not rescan metadata inside render.
  const segmentAnnotationsByGroup = useMemo(
    () => displaySegments.map((group) => (
      group.segments.map((segment) => getSegmentAnnotations(
        segment.timestampSeconds,
        segment.endTimestampSeconds,
      ))
    )),
    [displaySegments, getSegmentAnnotations]
  );

  useEffect(() => {
    setExpandedTopics((previous) => {
      const next = new Set(previous);
      let changed = false;

      displaySegments.forEach((_, index) => {
        if (!next.has(index)) {
          next.add(index);
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [displaySegments]);

  // Helper to get speaker name from labels
  const getSpeakerName = useCallback((speakerId: number | string | undefined): string => {
    if (speakerId === undefined) return '';
    const id = typeof speakerId === 'string' ? parseInt(speakerId, 10) : speakerId;
    return speakerLabels?.[id] || `Speaker ${speakerId}`;
  }, [speakerLabels]);

  // Generate edited transcript for chat context (excludes cut segments, uses speaker names)
  const editedTranscript = useMemo(() => {
    if (!isEditedMode || !displaySegments.length) {
      return transcript;
    }

    // Build transcript from edit-aware segments with timestamps and speaker names
    const lines: string[] = [];
    displaySegments.forEach(group => {
      // Add topic header
      if (group.topic?.title) {
        lines.push(`\n[${group.topic.start}] ${toPlainMetadataText(group.topic.title)}`);
      }
      // Add segment text with timestamps and proper speaker names
      group.segments.forEach(segment => {
        const timestamp = segment.timestamp || '';
        const speaker = segment.speaker !== undefined ? `${getSpeakerName(segment.speaker)}: ` : '';
        lines.push(`[${timestamp}] ${speaker}${segment.text}`);
      });
    });

    return lines.join('\n').trim();
  }, [isEditedMode, displaySegments, transcript, getSpeakerName]);

  // Find search matches in transcript
  // Note: onSearchUpdate is accessed via ref to avoid it being a dependency that causes infinite loops
  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      setLocalSearchMatches([]);
      if (onSearchUpdateRef.current) {
        onSearchUpdateRef.current([]);
      }

      // When search is cleared, scroll to the current playing segment only if playback sync is enabled.
      if (currentPlayingSegment && shouldFollowTranscript) {
        setTimeout(() => {
          const segmentKey = `${currentPlayingSegment.groupIndex}-${currentPlayingSegment.segmentIndex}`;
          const segmentElement = segmentRefs.current[segmentKey];
          if (segmentElement) {
            // First ensure the topic is expanded
            if (!expandedTopics.has(currentPlayingSegment.groupIndex)) {
              setExpandedTopics(prev => new Set(prev).add(currentPlayingSegment.groupIndex));
              // Wait for expansion animation
              setTimeout(() => {
                segmentElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
              }, 300);
            } else {
              segmentElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }
        }, 100); // Small delay to ensure DOM updates
      }
      return;
    }

    const matches: any[] = [];
    const searchLower = searchQuery.toLowerCase();

    // Search through all displayed segments (edit-aware)
    displaySegments.forEach((group, groupIndex) => {
      group.segments.forEach((segment, segmentIndex) => {
        const textLower = segment.text.toLowerCase();
        const matchIndex = textLower.indexOf(searchLower);

        if (matchIndex !== -1) {
          // Get surrounding context (100 chars before and after)
          const contextStart = Math.max(0, matchIndex - 100);
          const contextEnd = Math.min(segment.text.length, matchIndex + searchLower.length + 100);
          const contextText = segment.text.substring(contextStart, contextEnd);

          matches.push({
            groupIndex,
            segmentIndex,
            text: segment.text,
            contextText: contextText,
            contextStart: contextStart,
            matchIndex: matchIndex - contextStart,
            matchLength: searchLower.length,
            speaker: segment.speaker,
            timestamp: segment.timestamp,
            timestampSeconds: segment.timestampSeconds,
            endTimestampSeconds: segment.endTimestampSeconds,
            topic: group.topic,
            speakerColor: getSpeakerColor(segment.speaker)
          });
        }
      });
    });

    setLocalSearchMatches(matches);
    if (onSearchUpdateRef.current) {
      onSearchUpdateRef.current(matches);
    }
  }, [searchQuery, displaySegments, currentPlayingSegment, expandedTopics, getSpeakerColor, shouldFollowTranscript]);

  // Navigate to current search match
  const navigateToCurrentMatch = useCallback(() => {
    if (!searchMatches || searchMatches.length === 0 || currentMatchIndex < 0) return;
    
    const currentMatch = searchMatches[currentMatchIndex];
    if (!currentMatch) return;
    
    // First, expand the topic containing the match
    const groupIndex = currentMatch.groupIndex;
    if (!expandedTopics.has(groupIndex)) {
      setExpandedTopics(prev => new Set(prev).add(groupIndex));
      
      // Wait a bit for the animation to start before scrolling
      setTimeout(() => {
        // Build ref key
        const refKey = `segment-${currentMatch.groupIndex}-${currentMatch.segmentIndex}`;
        const element = searchMatchRefs.current[refKey];
        
        if (element) {
          // Scroll to element with offset for sticky header
          const yOffset = -200; // Adjust for sticky header and some padding
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          
          window.scrollTo({ top: y, behavior: 'smooth' });
          
          // Add highlight animation
          element.classList.add('search-highlight-animation');
          setTimeout(() => {
            element.classList.remove('search-highlight-animation');
          }, 2000);
        }
      }, 300); // Wait for expand animation
    } else {
      // Topic already expanded, scroll immediately
      const refKey = `segment-${currentMatch.groupIndex}-${currentMatch.segmentIndex}`;
      const element = searchMatchRefs.current[refKey];
      
      if (element) {
        // Scroll to element with offset for sticky header
        const yOffset = -200; // Adjust for sticky header and some padding
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        
        window.scrollTo({ top: y, behavior: 'smooth' });
        
        // Add highlight animation
        element.classList.add('search-highlight-animation');
        setTimeout(() => {
          element.classList.remove('search-highlight-animation');
        }, 2000);
      }
    }
  }, [searchMatches, currentMatchIndex, expandedTopics]);
  
  // Handle navigation when shouldNavigateToMatch changes
  useEffect(() => {
    if (shouldNavigateToMatch && searchMatches && searchMatches.length > 0) {
      navigateToCurrentMatch();
      if (onNavigationComplete) {
        onNavigationComplete();
      }
    }
  }, [shouldNavigateToMatch, navigateToCurrentMatch, onNavigationComplete, searchMatches]);

  // Check if this component is visible/active
  const [isComponentVisible, setIsComponentVisible] = useState(false);
  
  // Calculate comment counts for topics and segments
  const getCommentsForTimeRange = useCallback((startSeconds: number, endSeconds: number) => {
    // Filter root comments that are in the time range, but preserve their threaded structure
    const filteredComments = localComments.filter(comment => {
      if (!comment.timestampSeconds) return false;
      return comment.timestampSeconds >= startSeconds && comment.timestampSeconds <= endSeconds;
    });
    
    // The comments already have their replies attached from organizeCommentsIntoThreads
    // So we just need to return the filtered root comments with their existing thread structure
    return filteredComments;
  }, [localComments]);

  // Keep comment range scans out of the playback render path.
  const topicCommentsByGroup = useMemo(
    () => displaySegments.map((group) => getCommentsForTimeRange(
      timeToSeconds(group.topic.start),
      timeToSeconds(group.topic.end)
    )),
    [displaySegments, getCommentsForTimeRange, timeToSeconds]
  );

  const segmentCommentsByGroup = useMemo(
    () => displaySegments.map((group) => (
      group.segments.map((segment) => getCommentsForTimeRange(
        segment.timestampSeconds,
        segment.endTimestampSeconds
      ))
    )),
    [displaySegments, getCommentsForTimeRange]
  );

  // Sync with audio playback
  useEffect(() => {
    // Check if the component is mounted and visible
    const checkVisibility = () => {
      // For modal view or fullscreen tab, always consider visible
      if (isModal || isFullscreenTab) {
        setIsComponentVisible(true);
        return;
      }
      
      // For tab view, check if the readable transcript tab is active
      const readableTab = document.querySelector('[data-tab-id="readable"]');
      const container = document.querySelector('.readable-transcript-container') as HTMLElement;
      const isActive = readableTab?.classList.contains('data-[state=active]') || 
                      readableTab?.getAttribute('data-state') === 'active' ||
                      // Check parent container visibility
                      (container && container.offsetParent !== null);
      
      setIsComponentVisible(isActive || false);
    };
    
    // Initial check
    checkVisibility();
    
    // Set up observer for tab changes - only observe the specific tab
    const observer = new MutationObserver(checkVisibility);
    const readableTab = document.querySelector('[data-tab-id="readable"]');
    if (readableTab) {
      observer.observe(readableTab, { attributes: true, attributeFilter: ['class', 'data-state'] });
    }
    
    // Also listen for visibility changes
    document.addEventListener('visibilitychange', checkVisibility);
    
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', checkVisibility);
    };
  }, [isModal, isFullscreenTab]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const scrollContainer = transcriptScrollContainerRef.current;
      if (scrollContainer) {
        const nextScrollTop = scrollContainer.scrollTop;
        lastObservedScrollTopRef.current = nextScrollTop;
        setHasTranscriptScrolled(nextScrollTop > 12);
      }
      updateVisibleTopicGroupIndex();
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [currentPlayingSegment, displaySegments, expandedTopics, updateVisibleTopicGroupIndex]);

  // Track user scroll to disable auto-scroll temporarily
  useEffect(() => {
    const scrollContainer = transcriptScrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const disableFollowForUserIntent = () => {
      if (!shouldFollowTranscript) {
        return;
      }

      programmaticScrollGuardUntilRef.current = 0;
      lastScrolledSegmentRef.current = null;
      isScrollingRef.current = false;
      setCursorSyncEnabled(false);
    };

    const registerWheelIntent = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) < 2 && Math.abs(event.deltaX) < 2) {
        return;
      }

      lastUserScrollIntentAtRef.current = Date.now();
      disableFollowForUserIntent();
    };

    let lastTouchY: number | null = null;

    const registerTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches[0]?.clientY ?? null;
    };

    const registerTouchIntent = (event: TouchEvent) => {
      const nextTouchY = event.touches[0]?.clientY ?? null;
      if (lastTouchY !== null && nextTouchY !== null && Math.abs(nextTouchY - lastTouchY) < 4) {
        return;
      }

      lastUserScrollIntentAtRef.current = Date.now();
      disableFollowForUserIntent();
      lastTouchY = nextTouchY;
    };

    const registerPointerIntent = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || !scrollContainer.contains(event.target)) {
        return;
      }

      lastUserScrollIntentAtRef.current = Date.now();
    };

    const handleUserScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      const scrollDelta = Math.abs(scrollTop - lastObservedScrollTopRef.current);
      const recentUserIntent = (Date.now() - lastUserScrollIntentAtRef.current) <= 900;
      lastObservedScrollTopRef.current = scrollTop;
      setHasTranscriptScrolled(scrollTop > 12);
      updateVisibleTopicGroupIndex();

      if (
        isScrollingRef.current
        || !shouldFollowTranscript
      ) {
        return;
      }

      if (Date.now() < programmaticScrollGuardUntilRef.current && !recentUserIntent) {
        return;
      }

      if (!recentUserIntent && scrollDelta < 20) {
        return;
      }

      programmaticScrollGuardUntilRef.current = 0;
      lastScrolledSegmentRef.current = null;
      setCursorSyncEnabled(false);
    };

    scrollContainer.addEventListener('wheel', registerWheelIntent, { passive: true });
    scrollContainer.addEventListener('touchstart', registerTouchStart, { passive: true });
    scrollContainer.addEventListener('touchmove', registerTouchIntent, { passive: true });
    scrollContainer.addEventListener('pointerdown', registerPointerIntent, { passive: true });
    scrollContainer.addEventListener('scroll', handleUserScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('wheel', registerWheelIntent);
      scrollContainer.removeEventListener('touchstart', registerTouchStart);
      scrollContainer.removeEventListener('touchmove', registerTouchIntent);
      scrollContainer.removeEventListener('pointerdown', registerPointerIntent);
      scrollContainer.removeEventListener('scroll', handleUserScroll);
    };
  }, [setCursorSyncEnabled, shouldFollowTranscript, updateVisibleTopicGroupIndex]);
  
  // Track playing state changes
  useEffect(() => {
    lastPlayingStateRef.current = isPlaying;
  }, [isPlaying]);
  
  // Only update playing time when actually playing
  useEffect(() => {
    if (isPlaying) {
      setPlayingTime(currentTime);
    } else {
      setPlayingTime(undefined);
      lastScrolledSegmentRef.current = null;
    }
  }, [currentTime, isPlaying]);

  const findSegmentForTime = useCallback((targetTime: number | null | undefined) => {
    if (!Number.isFinite(targetTime)) {
      return null;
    }

    const safeTime = Number(targetTime);

    const checkSegment = (groupIndex: number, segmentIndex: number): boolean => {
      if (groupIndex >= displaySegments.length) return false;
      const group = displaySegments[groupIndex];
      if (segmentIndex >= group.segments.length) return false;
      const segment = group.segments[segmentIndex];
      return safeTime >= segment.timestampSeconds && safeTime <= segment.endTimestampSeconds;
    };

    const getNextPosition = (groupIndex: number, segmentIndex: number): { groupIndex: number; segmentIndex: number } | null => {
      const group = displaySegments[groupIndex];
      if (segmentIndex + 1 < group.segments.length) {
        return { groupIndex, segmentIndex: segmentIndex + 1 };
      }
      if (groupIndex + 1 < displaySegments.length) {
        return { groupIndex: groupIndex + 1, segmentIndex: 0 };
      }
      return null;
    };

    const getSegmentAtPosition = (groupIndex: number, segmentIndex: number): TranscriptSegment | null => {
      if (groupIndex >= displaySegments.length) return null;
      const group = displaySegments[groupIndex];
      if (segmentIndex >= group.segments.length) return null;
      return group.segments[segmentIndex] || null;
    };

    let foundPosition: { groupIndex: number; segmentIndex: number } | null = null;

    const lastPos = lastFoundPositionRef.current;
    if (lastPos) {
      if (checkSegment(lastPos.groupIndex, lastPos.segmentIndex)) {
        foundPosition = lastPos;
      } else {
        const nextPos = getNextPosition(lastPos.groupIndex, lastPos.segmentIndex);
        if (nextPos && checkSegment(nextPos.groupIndex, nextPos.segmentIndex)) {
          foundPosition = nextPos;
        } else if (nextPos) {
          const previousSegment = getSegmentAtPosition(lastPos.groupIndex, lastPos.segmentIndex);
          const nextSegment = getSegmentAtPosition(nextPos.groupIndex, nextPos.segmentIndex);
          if (
            previousSegment
            && nextSegment
            && safeTime >= previousSegment.endTimestampSeconds
            && safeTime < nextSegment.timestampSeconds
            && (nextSegment.timestampSeconds - previousSegment.endTimestampSeconds) <= SMALL_SEGMENT_GAP_SECONDS
          ) {
            foundPosition = lastPos;
          }
        }
      }
    }

    if (!foundPosition) {
      let previousPosition: { groupIndex: number; segmentIndex: number } | null = null;
      let nextPosition: { groupIndex: number; segmentIndex: number } | null = null;

      for (let groupIndex = 0; groupIndex < displaySegments.length; groupIndex++) {
        const group = displaySegments[groupIndex];
        const topicStart = timeToSeconds(group.topic.start);
        const topicEnd = timeToSeconds(group.topic.end);

        if (safeTime >= topicStart && safeTime <= topicEnd) {
          for (let segmentIndex = 0; segmentIndex < group.segments.length; segmentIndex++) {
            if (checkSegment(groupIndex, segmentIndex)) {
              foundPosition = { groupIndex, segmentIndex };
              break;
            }

            const segment = group.segments[segmentIndex];
            if (segment.endTimestampSeconds <= safeTime) {
              previousPosition = { groupIndex, segmentIndex };
            } else if (!nextPosition && segment.timestampSeconds > safeTime) {
              nextPosition = { groupIndex, segmentIndex };
              break;
            }
          }
          if (foundPosition) break;

          if (
            !foundPosition
            && previousPosition
            && nextPosition
          ) {
            const previousSegment = getSegmentAtPosition(previousPosition.groupIndex, previousPosition.segmentIndex);
            const nextSegment = getSegmentAtPosition(nextPosition.groupIndex, nextPosition.segmentIndex);
            if (
              previousSegment
              && nextSegment
              && safeTime >= previousSegment.endTimestampSeconds
              && safeTime < nextSegment.timestampSeconds
              && (nextSegment.timestampSeconds - previousSegment.endTimestampSeconds) <= SMALL_SEGMENT_GAP_SECONDS
            ) {
              foundPosition = previousPosition;
              break;
            }
          }
        }
      }
    }

    return foundPosition;
  }, [displaySegments, timeToSeconds]);

  // Sync with audio playback (only when playing and visible)
  // OPTIMIZED: Uses cached position for O(1) lookup when time moves forward
  useEffect(() => {
    // ABSOLUTELY NO UPDATES when not playing
    if (!isPlaying) {
      return;
    }

    // Only run this effect when we have a playing time
    if (playingTime === undefined || !isComponentVisible) return;

    const activeWord = currentWordIndex >= 0 ? wordIndex[currentWordIndex] : null;
    const activeWordPosition = activeWord
      ? wordSegmentPositions.get(buildTranscriptWordKey(activeWord)) || null
      : null;
    const foundPosition = activeWordPosition || findSegmentForTime(playingTime);

    // Update state if we found a segment
    if (foundPosition) {
      // Cache the found position for next lookup
      lastFoundPositionRef.current = foundPosition;

      const isNewSegment = !currentPlayingSegment ||
        currentPlayingSegment.groupIndex !== foundPosition.groupIndex ||
        currentPlayingSegment.segmentIndex !== foundPosition.segmentIndex;

      if (isNewSegment) {
        setCurrentPlayingSegment(foundPosition);

        // Expand topic if not already
        const { groupIndex } = foundPosition;
        if (!expandedTopics.has(groupIndex)) {
          setExpandedTopics((previous) => new Set(previous).add(groupIndex));
        }
      }
    } else {
      // Clear current segment if none found
      lastFoundPositionRef.current = null;
      if (currentPlayingSegment) {
        setCurrentPlayingSegment(null);
      }
    }
  }, [playingTime, isPlaying, currentPlayingSegment, currentWordIndex, expandedTopics, findSegmentForTime, isComponentVisible, shouldFollowTranscript, wordIndex, wordSegmentPositions]);
  
  useEffect(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    if (!isPlaying) {
      isScrollingRef.current = false;
      return;
    }

    if (!shouldFollowTranscript || !currentPlayingSegment || !isComponentVisible) {
      return;
    }

    const scrollResetDelayMs = perfSettings.isMobile
      ? 1400
      : (perfSettings.useSmoothScroll ? 1000 : 250);
    const followCheckIntervalMs = perfSettings.isCompactTouchLayout ? 650 : 450;

    const scrollToCurrentSegment = (forceRecenter: boolean) => {
      if (!isPlaying || !currentPlayingSegment || !shouldFollowTranscript || isScrollingRef.current) {
        return;
      }

      const alreadyAtCurrentSegment =
        lastScrolledSegmentRef.current?.groupIndex === currentPlayingSegment.groupIndex
        && lastScrolledSegmentRef.current?.segmentIndex === currentPlayingSegment.segmentIndex;

      if (!forceRecenter && alreadyAtCurrentSegment) {
        return;
      }

      if (forceRecenter && !shouldRecenterTranscriptTarget(currentPlayingSegment.groupIndex, currentPlayingSegment.segmentIndex)) {
        return;
      }

      const segmentKey = `${currentPlayingSegment.groupIndex}-${currentPlayingSegment.segmentIndex}`;
      const segmentElement = segmentRefs.current[segmentKey];

      if (!segmentElement) {
        return;
      }

      isScrollingRef.current = true;
      window.requestAnimationFrame(() => {
        if (isPlaying && shouldFollowTranscript) {
          scrollTranscriptTarget(currentPlayingSegment.groupIndex, currentPlayingSegment.segmentIndex);
          lastScrolledSegmentRef.current = currentPlayingSegment;
        }
        window.setTimeout(() => {
          isScrollingRef.current = false;
        }, scrollResetDelayMs);
      });
    };

    const initialScrollTimer = window.setTimeout(() => {
      scrollToCurrentSegment(false);
    }, 300);
    scrollIntervalRef.current = setInterval(() => {
      scrollToCurrentSegment(true);
    }, followCheckIntervalMs);

    return () => {
      window.clearTimeout(initialScrollTimer);
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, [
    currentPlayingSegment,
    isComponentVisible,
    isPlaying,
    perfSettings.isCompactTouchLayout,
    perfSettings.isMobile,
    perfSettings.useSmoothScroll,
    scrollTranscriptTarget,
    shouldFollowTranscript,
    shouldRecenterTranscriptTarget,
  ]);

  useEffect(() => {
    if (
      !syncRequestId
      || lastHandledSyncRequestIdRef.current === syncRequestId
    ) {
      return;
    }

    lastHandledSyncRequestIdRef.current = syncRequestId;
    lastScrolledSegmentRef.current = null;
    const targetTime = syncRequestTargetTime ?? currentTime;
    const resolvedSegment = findSegmentForTime(targetTime);
    if (!resolvedSegment) {
      return;
    }

    lastFoundPositionRef.current = resolvedSegment;
    setCurrentPlayingSegment((previous) => (
      previous?.groupIndex === resolvedSegment.groupIndex
      && previous?.segmentIndex === resolvedSegment.segmentIndex
        ? previous
        : resolvedSegment
    ));
    setExpandedTopics((previous) => {
      if (previous.has(resolvedSegment.groupIndex)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(resolvedSegment.groupIndex);
      return next;
    });

    isScrollingRef.current = true;
    const frameId = window.requestAnimationFrame(() => {
      scrollTranscriptTarget(resolvedSegment.groupIndex, resolvedSegment.segmentIndex);
    });
    const resetTimer = window.setTimeout(() => {
      isScrollingRef.current = false;
    }, perfSettings.isMobile ? 1400 : (perfSettings.useSmoothScroll ? 1000 : 250));

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(resetTimer);
      isScrollingRef.current = false;
    };
  }, [currentTime, findSegmentForTime, perfSettings.isMobile, perfSettings.useSmoothScroll, scrollTranscriptTarget, syncRequestId, syncRequestTargetTime]);

  const speakerChipFontSize = Math.max(11, Math.min(16, Math.round(typographyConfig.fontSize * 0.72)));
  const speakerChipDotSize = Math.max(7, Math.min(10, Math.round(typographyConfig.fontSize * 0.13)));

  const content = (
    <>
      <div className={`readable-transcript-container ${isModal ? '' : 'h-full'} flex overflow-hidden`}>
      {/* Collapsible Chat Panel */}
      {showChat && (
        <AnimatePresence>
          {isChatOpen && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: '400px', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-l-xl overflow-hidden"
            >
              <div className="h-full flex flex-col">
                {/* Chat Header */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <MessageCircle size={20} className="text-[#FFB52B]" />
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">TTML Chat</h4>
                    </div>
                    <button
                      onClick={() => setIsChatOpen(false)}
                      className="p-1 text-gray-500 hover:text-gray-700 rounded"
                    >
                      <ChevronLeft size={18} />
                    </button>
                  </div>
                  
                  {/* TTML Toggle */}
                  <div className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      id="include-ttml"
                      checked={includeTtmlInChat}
                      onChange={(e) => setIncludeTtmlInChat(e.target.checked)}
                      className="w-4 h-4 text-[#232E3F] bg-gray-100 border-gray-300 rounded focus:ring-[#ffcf74] dark:focus:ring-[#ffcf74] dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label htmlFor="include-ttml" className="text-gray-700 dark:text-gray-300">
                      Include TTML ({filename}) in context
                    </label>
                  </div>
                </div>
                
                {/* Chat Interface */}
                <div className="flex-1 min-h-0">
                  {supportedModels.length > 0 && selectedChatModel ? (
                    <ChatInterface
                      transcript={editedTranscript}
                      model={selectedChatModel}
                      onModelChange={setSelectedChatModel}
                      supportedModels={supportedModels}
                      currentTtmlFiles={includeTtmlInChat ? [{ name: filename, content: editedTtmlContent }] : []}
                      sessionId={sessionId}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="animate-spin text-gray-400" size={24} />
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Audio Status Panel */}
        {isModal && (audioStatus.error || audioStatus.isBlob || isOrphanSession) && (
          <div className="p-4 border-b border-gray-200 bg-amber-50">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {audioStatus.available ? (
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                ) : (
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-amber-800">Audio Status</h4>
                  {audioStatus.url && (
                    <a
                      href={audioStatus.url}
                      download="audio.m4a"
                      className="text-xs bg-amber-600 text-white px-2 py-1 rounded hover:bg-amber-700 transition-colors"
                    >
                      Download Blob
                    </a>
                  )}
                </div>
                
                <div className="text-xs text-amber-700 space-y-1">
                  <div>
                    <span className="font-medium">Available:</span> {audioStatus.available ? 'Yes' : 'No'}
                  </div>
                  <div>
                    <span className="font-medium">Type:</span> {audioStatus.isBlob ? 'Blob URL (may have range request issues)' : audioFile ? 'Direct File' : 'None'}
                  </div>
                  {audioStatus.error && (
                    <div className="text-red-700">
                      <span className="font-medium">Error:</span> {audioStatus.error}
                    </div>
                  )}
                </div>
                
                {(audioStatus.error?.includes('range request') || audioStatus.isBlob) && !isOrphanSession && (
                  <div className="mt-2 text-xs text-amber-600">
                    ⚠️ Blob URLs don't fully support range requests needed for audio scrubbing. Consider uploading the original audio file for better playback.
                  </div>
                )}
                
                {!isOrphanSession && sessionId && transcriptionChecksum && filename && (
                  <button
                    onClick={() => setIsOrphanSession(true)}
                    className="mt-2 text-xs bg-amber-600 text-white px-3 py-1 rounded hover:bg-amber-700 transition-colors"
                  >
                    Upload Original Audio
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Orphan Session Recovery */}
        {isModal && isOrphanSession && sessionId && transcriptionChecksum && filename && (
          <div className="p-6 border-b border-gray-200 bg-yellow-50">
            <OrphanSessionRecovery
              sessionId={sessionId}
              filename={filename}
              transcriptionChecksum={transcriptionChecksum}
              onRecoverySuccess={() => {
                setIsOrphanSession(false);
                setOrphanError('');
                setAudioStatus({ available: false, isBlob: false });
                window.location.reload(); // Reload to refresh all audio
              }}
              className="max-w-none"
            />
          </div>
        )}
        {/* Content */}
        <div
          ref={transcriptScrollContainerRef}
          className={`flex-1 overflow-y-auto relative ${perfSettings.isMobile ? 'px-0 pt-0 pb-6' : 'px-6 pt-0 pb-6'}`}
          style={{
            fontFamily: fontFamilies[typographyConfig.fontFamily],
            fontSize: `${typographyConfig.fontSize}px`,
            lineHeight: typographyConfig.lineHeight,
            textAlign: typographyConfig.textAlign,
          }}
        >
          {stickyCurrentTopicGroup && (
            <div
              className={`sticky top-0 z-30 mb-3 border-y shadow-sm backdrop-blur ${
                perfSettings.isMobile ? '' : '-mx-6'
              } ${getRankingColor(stickyCurrentTopicGroup.topic, true)}`}
              data-sticky-current-topic="true"
            >
              <div className={`${perfSettings.isMobile ? 'px-3 py-2' : 'px-6 py-3'}`}>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-70">
                  Current Topic
                </div>
                <div className="truncate text-sm font-semibold">
                  {currentTopicTitle}
                </div>
                {currentTopicDescription && (
                  <div className="mt-1 line-clamp-2 text-xs leading-snug text-[#5B687C] dark:text-[#CBD2DB]">
                    {currentTopicDescription}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Floating Action Buttons */}
          <div className="fixed top-4 right-4 flex items-center space-x-2 z-20">
            {showChat && !isChatOpen && (
              <button
                onClick={() => setIsChatOpen(true)}
                className="p-2 bg-white dark:bg-gray-800 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg shadow-lg transition-colors"
                title="Open TTML Chat"
              >
                <MessageCircle size={20} />
              </button>
            )}
            {isModal && onClose && (
              <button
                onClick={onClose}
                className="p-2 bg-white dark:bg-gray-800 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg shadow-lg transition-colors"
              >
                <X size={20} />
              </button>
            )}
          </div>
          
          {/* Edit Mode Toggle Banner */}
          {hasEditCuts && (
            <div className={`mb-4 px-4 py-3 rounded-lg border-2 ${
              isEditedMode
                ? 'bg-[#fff1cc] dark:bg-[#4b3913]/35 border-[#FFB52B] dark:border-[#d5a145]'
                : 'bg-amber-50 dark:bg-amber-900/20 border-amber-400 dark:border-amber-600'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Scissors size={20} className={isEditedMode ? 'text-[#8a6115] dark:text-[#ffcf74]' : 'text-amber-600 dark:text-amber-400'} />
                  <div>
                    <span className={`text-sm font-semibold ${
                      isEditedMode
                        ? 'text-[#6e4d0e] dark:text-[#ffe09b]'
                        : 'text-amber-800 dark:text-amber-200'
                    }`}>
                      {isEditedMode
                        ? `Edited View`
                        : `Original View`
                      }
                    </span>
                    <span className={`ml-2 text-xs ${
                      isEditedMode
                        ? 'text-[#8a6115] dark:text-[#ffcf74]'
                        : 'text-amber-600 dark:text-amber-400'
                    }`}>
                      {isEditedMode
                        ? `${timelineMapper?.getCuts().length || 0} cuts applied • ${timelineMapper ? formatTimestampLocal(timelineMapper.getStats().editedDuration) : ''}`
                        : `${timelineMapper?.getCuts().length || 0} cuts available • ${timelineMapper ? formatTimestampLocal(timelineMapper.getStats().originalDuration) : ''}`
                      }
                    </span>
                  </div>
                </div>

                {/* Toggle Switch */}
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium ${!isEditedMode ? 'text-amber-700 dark:text-amber-300' : 'text-gray-400 dark:text-gray-500'}`}>
                    Original
                  </span>
                  <button
                    onClick={toggleEditMode}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      isEditedMode
                        ? 'bg-[#232E3F] dark:bg-[#314159] focus:ring-[#ffcf74]'
                        : 'bg-amber-500 dark:bg-amber-600 focus:ring-amber-500'
                    }`}
                    role="switch"
                    aria-checked={isEditedMode}
                    aria-label="Toggle between original and edited view"
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                        isEditedMode ? 'translate-x-8' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-xs font-medium ${isEditedMode ? 'text-[#8a6115] dark:text-[#ffcf74]' : 'text-gray-400 dark:text-gray-500'}`}>
                    Edited
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Show search results when searching, otherwise show normal transcript */}
          <div
            className={perfSettings.isMobile ? 'w-full' : 'mx-auto w-full'}
            style={{ maxWidth: perfSettings.isMobile ? '100%' : maxWidths[typographyConfig.maxWidth] }}
          >
          {searchQuery && searchQuery.trim() !== '' ? (
            // Search Results View
            <div className="space-y-4">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <Search size={20} className="text-gray-600 dark:text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Search Results
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Found {localSearchMatches.length} match{localSearchMatches.length !== 1 ? 'es' : ''} for "{searchQuery}"
                  </p>
                </div>
              </div>
              
              {localSearchMatches.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No matches found for "{searchQuery}"
                </div>
              ) : (
                <div className="space-y-4" key={visualizationKey}>
                  {localSearchMatches.map((match, index) => {
                    const speakerColor = match.speakerColor;
                    const topicColor = getRankingColor(match.topic, false);
                    
                    return (
                      <div
                        key={index}
                        className={`group rounded-lg p-4 shadow-sm transition-all duration-300 cursor-pointer
                          hover:shadow-lg bg-white dark:bg-gray-800 relative overflow-hidden`}
                        style={{
                          borderColor: isDarkMode ? 'rgba(57, 70, 90, 1)' : 'rgba(216, 222, 231, 1)',
                          borderWidth: '1px',
                          borderStyle: 'solid'
                        }}
                        onClick={() => {
                          // Seek to this position
                          if (onPlaySegment) {
                            onPlaySegment(match.timestampSeconds, match.endTimestampSeconds);
                          }
                        }}
                      >
                        {/* Hover background effect */}
                        <div 
                          className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-300"
                          style={{ backgroundColor: speakerColor }}
                        />
                        {/* Content wrapper with relative positioning */}
                        <div className="relative z-10">
                          {/* Topic header */}
                          <div className={`-mx-4 -mt-4 mb-3 px-4 py-2 ${topicColor} rounded-t-lg`}>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium hidden md:inline">
                              Segment {match.topic.segmentNumber}
                            </span>
                            <span className="text-xs truncate">
                              {toPlainMetadataText(match.topic.title)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Speaker and timestamp */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {/* Speaker avatar */}
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                              style={{ backgroundColor: speakerColor }}
                            >
                              {match.speaker.charAt(0)}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {match.speaker}
                            </span>
                          </div>
                          <span className="hidden md:inline text-sm text-gray-500 dark:text-gray-400">
                            {match.timestamp}
                          </span>
                        </div>
                        
                        {/* Search result text with highlighting */}
                        <div
                          className="text-gray-700 dark:text-gray-300"
                          style={{
                            fontSize: `${typographyConfig.fontSize}px`,
                            lineHeight: typographyConfig.lineHeight,
                            textAlign: typographyConfig.textAlign
                          }}
                        >
                          {match.contextStart > 0 && (
                            <span className="text-gray-500">...</span>
                          )}
                          {match.contextText.substring(0, match.matchIndex)}
                          <span className="bg-yellow-300 dark:bg-yellow-600 px-1 rounded font-semibold">
                            {match.contextText.substring(match.matchIndex, match.matchIndex + match.matchLength)}
                          </span>
                          {match.contextText.substring(match.matchIndex + match.matchLength)}
                          {match.contextStart + match.contextText.length < match.text.length && (
                            <span className="text-gray-500">...</span>
                          )}
                        </div>
                        
                          {/* Play indicator on hover */}
                          <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="bg-[#FFB52B] text-[#232E3F] p-2 rounded-bl-lg rounded-tr-lg shadow-lg">
                              <Play size={16} fill="currentColor" />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            // Normal Transcript View - key forces re-render when visualization mode changes
            <div className="space-y-4" key={visualizationKey}>
              {displaySegments.map((group, groupIndex) => (
                <div
                  key={groupIndex}
                  ref={el => { if (el) topicRefs.current[groupIndex] = el; }}
                  className={`relative overflow-hidden rounded-xl border ${getRankingColor(group.topic, false)}`}
                  style={{
                    contentVisibility: 'auto',
                    containIntrinsicSize: perfSettings.isCompactTouchLayout ? '360px' : '520px',
                  }}
                >
                  <div className={`relative z-10 border-b ${getRankingColor(group.topic, true)}`}>
                    <div className="flex items-center gap-3 px-3 py-2">
                      <button
                        onClick={() => toggleTopic(groupIndex)}
                        className="min-w-0 flex-1 text-left transition-colors hover:opacity-80"
                      >
                        <div className="hidden truncate text-sm font-semibold leading-tight md:block md:text-base">
                          {toPlainMetadataText(group.topic.title) || 'Untitled topic'}
                        </div>
                        <div className="block md:hidden">
                          <OverflowBounceText 
                            text={toPlainMetadataText(group.topic.title) || 'Untitled topic'}
                            className="w-full"
                          />
                        </div>
                        {getTopicDescription(group.topic) && (
                          <div className="mt-1 text-xs leading-snug text-[#5B687C] dark:text-[#CBD2DB] md:text-sm">
                            {getTopicDescription(group.topic)}
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                          {(() => {
                            const timeInfo = getTopicTimeInfo(group.topic);
                            return (
                              <>
                                <span className="inline-flex items-center gap-1.5">
                                  <Clock size={12} />
                                  <span>{timeInfo.startTime} - {timeInfo.endTime}</span>
                                </span>
                                <span className="font-medium">{timeInfo.duration}</span>
                              </>
                            );
                          })()}
                        </div>
                      </button>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const topicComments = topicCommentsByGroup[groupIndex] || EMPTY_COMMENTS;
                          const commentCount = topicComments.length;
                          if (commentCount > 0) {
                            return (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setShowCommentPopup({
                                    comments: topicComments,
                                    position: { x: rect.left, y: rect.bottom + 5 }
                                  });
                                }}
                                className="text-xs px-2 py-1 rounded-full bg-[#fff1cc] dark:bg-[#4b3913]/60 text-[#6e4d0e] dark:text-[#ffe09b] hover:bg-[#ffe4a3] dark:hover:bg-[#5a4417] transition-colors flex items-center gap-1"
                              >
                                <MessageCircle size={12} />
                                {commentCount}
                              </button>
                            );
                          }
                          return null;
                        })()}
                        <button
                          onClick={() => toggleTopic(groupIndex)}
                          className="rounded-lg p-1 text-gray-600 transition-colors hover:bg-black/5 dark:text-gray-400"
                          title={expandedTopics.has(groupIndex) ? 'Collapse topic' : 'Expand topic'}
                        >
                          {expandedTopics.has(groupIndex) ? (
                            <ChevronDown size={18} className="text-gray-600 dark:text-gray-400" />
                          ) : (
                            <ChevronRight size={18} className="text-gray-600 dark:text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedTopics.has(groupIndex) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="border-t border-gray-200/70 dark:border-gray-700/70"
                      >
                        <div className="divide-y divide-gray-200/70 px-4 py-2 dark:divide-gray-700/70">
                          {group.segments.map((segment, segmentIndex) => {
                            const speakerColor = getSpeakerColor(segment.speaker);
                            const annotations = segmentAnnotationsByGroup[groupIndex]?.[segmentIndex] || EMPTY_SEGMENT_ANNOTATIONS;
                            const segmentComments = segmentCommentsByGroup[groupIndex]?.[segmentIndex] || EMPTY_COMMENTS;
                            const segmentCommentCount = segmentComments.length;
                            const isSegmentActive =
                              (playingSegment?.groupIndex === groupIndex && playingSegment?.segmentIndex === segmentIndex) ||
                              (currentPlayingSegment?.groupIndex === groupIndex && currentPlayingSegment?.segmentIndex === segmentIndex);

                            return (
                              <div
                                key={segmentIndex}
                                ref={el => {
                                  if (el) {
                                    segmentRefs.current[`${groupIndex}-${segmentIndex}`] = el;
                                    searchMatchRefs.current[`segment-${groupIndex}-${segmentIndex}`] = el;
                                  }
                                }}
                                className={`py-4 transition-colors ${isSegmentActive ? 'bg-brand-yellow/5' : ''}`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div
                                      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${
                                        isDarkMode ? 'text-[#F4F4F4]' : ''
                                      }`}
                                      style={{
                                        color: isDarkMode ? '#F4F4F4' : speakerColor,
                                        backgroundColor: withAlpha(speakerColor, isDarkMode ? 0.24 : 0.12),
                                        borderColor: withAlpha(speakerColor, isDarkMode ? 0.45 : 0.28),
                                        fontSize: `${speakerChipFontSize}px`,
                                      }}
                                    >
                                      <span
                                        className="rounded-full"
                                        style={{
                                          backgroundColor: speakerColor,
                                          width: `${speakerChipDotSize}px`,
                                          height: `${speakerChipDotSize}px`,
                                        }}
                                      />
                                      <span className="truncate">{segment.speaker}</span>
                                    </div>
                                  </div>
                                  <div className="flex shrink-0 items-center gap-2">
                                    {annotations.references.length > 0 && (
                                      <button
                                        onClick={() => {
                                          setShowAnnotationPopup({
                                            groupIndex,
                                            segmentIndex,
                                            type: 'references'
                                          });
                                        }}
                                        className={`rounded-full px-2 py-1 text-[11px] font-medium transition-colors ${
                                          isDarkMode
                                            ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                        }`}
                                        title={`${annotations.references.length} reference${annotations.references.length > 1 ? 's' : ''}`}
                                      >
                                        {annotations.references.length} ref
                                      </button>
                                    )}
                                    {segmentCommentCount > 0 && (
                                      <button
                                        onClick={(event) => {
                                          const rect = event.currentTarget.getBoundingClientRect();
                                          setShowCommentPopup({
                                            comments: segmentComments,
                                            position: { x: rect.left, y: rect.bottom + 5 },
                                            timestampSeconds: segment.timestampSeconds
                                          });
                                        }}
                                        className="inline-flex items-center gap-1 rounded-full bg-[#fff1cc] px-2 py-1 text-[11px] font-medium text-[#6e4d0e] transition-colors hover:bg-[#ffe4a3] dark:bg-[#4b3913]/60 dark:text-[#ffe09b] dark:hover:bg-[#5a4417]"
                                        title={`${segmentCommentCount} comment${segmentCommentCount > 1 ? 's' : ''}`}
                                      >
                                        <MessageCircle size={11} />
                                        {segmentCommentCount}
                                      </button>
                                    )}
                                    {segment.timestamp && (
                                      <button
                                        onClick={() => {
                                          playAudioSegment(groupIndex, segmentIndex, segment.timestampSeconds, segment.endTimestampSeconds);
                                        }}
                                        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium transition-colors ${
                                          isDarkMode
                                            ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                            : 'bg-white/80 text-gray-600 hover:bg-white'
                                        }`}
                                        title="Play from segment start"
                                      >
                                        <Clock size={11} />
                                        {segment.timestamp}
                                      </button>
                                    )}
                                  </div>
                                </div>

                                <div
                                  className="text-gray-700 dark:text-gray-200"
                                  style={{
                                    fontSize: `${typographyConfig.fontSize}px`,
                                    lineHeight: typographyConfig.lineHeight,
                                    textAlign: typographyConfig.textAlign
                                  }}
                                >
                                  <OptimizedHighlightedText
                                    text={segment.text}
                                    startTime={segment.timestampSeconds}
                                    endTime={segment.endTimestampSeconds}
                                    currentWordIndex={currentWordIndex}
                                    segmentWords={(segment as any).words || []}
                                    wordIndex={wordIndex}
                                    isActive={isSegmentActive}
                                    isPlaying={isLocalPlaying || isPlaying}
                                    className="mt-2 block"
                                    searchQuery={searchQuery}
                                    dragHighlightTime={dragHighlightTime}
                                    clipRanges={annotations.clipRanges}
                                    editRanges={annotations.editRanges}
                                    isDarkMode={isDarkMode}
                                    onWordClick={async (time) => {
                                      seekTo(time);
                                      requestCursorSync(time);
                                      await play();
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })}

                          {group.segments.length === 0 && (
                            <div className="py-4 text-center text-sm text-gray-500">
                              No transcript segments found for this topic
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
          </div>

        </div>
        
      </div> {/* Main Content */}
    </div>
    </>
  );

  if (isModal) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] flex"
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </motion.div>

        {/* Annotation Popup Modal */}
        <AnimatePresence>
          {showAnnotationPopup && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4"
              onClick={() => setShowAnnotationPopup(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                    {showAnnotationPopup.type === 'clips' ? (
                      <>
                        <span>🎬</span>
                        <span>Clips</span>
                      </>
                    ) : showAnnotationPopup.type === 'references' ? (
                      <>
                        <span>📚</span>
                        <span>References</span>
                      </>
                    ) : (
                      <>
                        <span>✂️</span>
                        <span>Edits</span>
                      </>
                    )}
                  </h3>
                  <button
                    onClick={() => setShowAnnotationPopup(null)}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
                
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                  {(() => {
                    const group = groupedSegments[showAnnotationPopup.groupIndex];
                    const segment = group.segments[showAnnotationPopup.segmentIndex];
                    const annotations = getSegmentAnnotations(
                      segment.timestampSeconds, 
                      segment.endTimestampSeconds
                    );
                    
                    const items = showAnnotationPopup.type === 'clips' 
                      ? annotations.clips 
                      : showAnnotationPopup.type === 'references' 
                        ? annotations.references 
                        : annotations.edits;
                    
                    if (items.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                          No {showAnnotationPopup.type} found for this segment.
                        </div>
                      );
                    }
                    
                    return (
                      <div className="space-y-4">
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                          <strong>Segment:</strong> {segment.speaker} at {segment.timestamp}
                        </div>
                        
                        {items.map((item, index) => {
                          // Reference-specific rendering
                          if (showAnnotationPopup.type === 'references') {
                            const parsed = parseReferenceContent(item.content);
                            const startSeconds = timestampToSeconds(item.start);
                            return (
                              <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 space-y-3">
                                {/* Title with optional URL indicator */}
                                <div className="flex items-start justify-between gap-2">
                                  <h4 className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    {parsed.url && <Link2 size={16} className="text-[#FFB52B] flex-shrink-0" />}
                                    {parsed.title}
                                  </h4>
                                </div>

                                {/* Category badge */}
                                {parsed.category && (
                                  <div>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#edf0f4] dark:bg-[#314159] text-[#455468] dark:text-[#d9e2f1]">
                                      {parsed.category}
                                    </span>
                                  </div>
                                )}

                                {/* Context */}
                                {parsed.context && (
                                  <p className="text-sm text-gray-600 dark:text-gray-300">
                                    {parsed.context}
                                  </p>
                                )}

                                {/* URL Link */}
                                {parsed.url && (
                                  <a
                                    href={parsed.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#fff1cc] dark:bg-[#4b3913]/45 text-[#6e4d0e] dark:text-[#ffe09b] hover:bg-[#ffe4a3] dark:hover:bg-[#5a4417] transition-colors text-sm"
                                  >
                                    <ExternalLink size={14} />
                                    <span className="truncate">{parsed.linkText || parsed.url}</span>
                                  </a>
                                )}

                                {/* Timestamp button */}
                                <button
                                  onClick={() => {
                                    seekTo(startSeconds);
                                    setShowAnnotationPopup(null);
                                  }}
                                  className="flex items-center gap-1.5 px-2 py-1 text-xs rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                                >
                                  <Clock size={12} />
                                  {item.start}
                                </button>
                              </div>
                            );
                          }

                          // Edit-specific rendering
                          if (showAnnotationPopup.type === 'edits') {
                            const parsed = parseEditContent(item.content);
                            const startSeconds = timestampToSeconds(item.start);
                            return (
                              <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 space-y-3">
                                {/* Title if present */}
                                {parsed.title && (
                                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                                    {parsed.title}
                                  </h4>
                                )}

                                {/* Priority badge if present */}
                                {parsed.priority && (
                                  <div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      parsed.priority.toLowerCase().includes('high')
                                        ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                                        : parsed.priority.toLowerCase().includes('medium')
                                          ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300'
                                          : 'bg-[#edf0f4] dark:bg-[#314159] text-[#455468] dark:text-[#d9e2f1]'
                                    }`}>
                                      Priority: {parsed.priority}
                                    </span>
                                  </div>
                                )}

                                {/* Main suggestion */}
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                  <span className="font-medium text-gray-900 dark:text-gray-100">Suggestion: </span>
                                  {parsed.suggestion}
                                </div>

                                {/* Improvement if present */}
                                {parsed.improvement && (
                                  <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-[#FFB52B] dark:border-[#d5a145]">
                                    <span className="font-medium text-[#8a6115] dark:text-[#ffcf74]">Improvement: </span>
                                    {parsed.improvement}
                                  </div>
                                )}

                                {/* Note if present */}
                                {parsed.note && (
                                  <div className="text-xs text-gray-500 dark:text-gray-500 italic">
                                    Note: {parsed.note}
                                  </div>
                                )}

                                {/* Timestamp button */}
                                <button
                                  onClick={() => {
                                    seekTo(startSeconds);
                                    setShowAnnotationPopup(null);
                                  }}
                                  className="flex items-center gap-1.5 px-2 py-1 text-xs rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                                >
                                  <Clock size={12} />
                                  {item.start}
                                </button>
                              </div>
                            );
                          }

                          // Default rendering for clips
                          return (
                            <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                  Clip {index + 1}
                                </h4>
                                <button
                                  onClick={() => {
                                    seekTo(timestampToSeconds(item.start));
                                    setShowAnnotationPopup(null);
                                  }}
                                  className="flex items-center gap-1.5 px-2 py-1 text-xs rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                                >
                                  <Clock size={12} />
                                  {item.start}
                                </button>
                              </div>

                              <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                {item.title || item.content.replace(/\[CLIP\]\s*/g, '').trim()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800">
                  <button
                    onClick={() => setShowAnnotationPopup(null)}
                    className="w-full py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  return (
    <>
      {content}
      
      {/* Annotation Popup Modal for non-modal mode */}
      <AnimatePresence>
        {showAnnotationPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={() => setShowAnnotationPopup(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                  {showAnnotationPopup.type === 'clips' ? (
                    <>
                      <span>🎬</span>
                      <span>Clips</span>
                    </>
                  ) : showAnnotationPopup.type === 'references' ? (
                    <>
                      <span>📚</span>
                      <span>References</span>
                    </>
                  ) : (
                    <>
                      <span>✂️</span>
                      <span>Edits</span>
                    </>
                  )}
                </h3>
                <button
                  onClick={() => setShowAnnotationPopup(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {(() => {
                  const group = groupedSegments[showAnnotationPopup.groupIndex];
                  const segment = group.segments[showAnnotationPopup.segmentIndex];
                  const annotations = getSegmentAnnotations(
                    segment.timestampSeconds, 
                    segment.endTimestampSeconds
                  );
                  
                  const items = showAnnotationPopup.type === 'clips' 
                    ? annotations.clips 
                    : showAnnotationPopup.type === 'references' 
                      ? annotations.references 
                      : annotations.edits;
                  
                  if (items.length === 0) {
                    return (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No {showAnnotationPopup.type} found for this segment.
                      </div>
                    );
                  }
                  
                  return (
                    <div className="space-y-4">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        <strong>Segment:</strong> {segment.speaker} at {segment.timestamp}
                      </div>

                      {items.map((item, index) => {
                        // Reference-specific rendering
                        if (showAnnotationPopup.type === 'references') {
                          const parsed = parseReferenceContent(item.content);
                          const startSeconds = timestampToSeconds(item.start);
                          return (
                            <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 space-y-3">
                              {/* Title with optional URL indicator */}
                              <div className="flex items-start justify-between gap-2">
                                <h4 className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                  {parsed.url && <Link2 size={16} className="text-[#FFB52B] flex-shrink-0" />}
                                  {parsed.title}
                                </h4>
                              </div>

                              {/* Category badge */}
                              {parsed.category && (
                                <div>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#edf0f4] dark:bg-[#314159] text-[#455468] dark:text-[#d9e2f1]">
                                    {parsed.category}
                                  </span>
                                </div>
                              )}

                              {/* Context */}
                              {parsed.context && (
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                  {parsed.context}
                                </p>
                              )}

                              {/* URL Link */}
                              {parsed.url && (
                                <a
                                  href={parsed.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#fff1cc] dark:bg-[#4b3913]/45 text-[#6e4d0e] dark:text-[#ffe09b] hover:bg-[#ffe4a3] dark:hover:bg-[#5a4417] transition-colors text-sm"
                                >
                                  <ExternalLink size={14} />
                                  <span className="truncate">{parsed.linkText || parsed.url}</span>
                                </a>
                              )}

                              {/* Timestamp button */}
                              <button
                                onClick={() => {
                                  seekTo(startSeconds);
                                  setShowAnnotationPopup(null);
                                }}
                                className="flex items-center gap-1.5 px-2 py-1 text-xs rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                              >
                                <Clock size={12} />
                                {item.start}
                              </button>
                            </div>
                          );
                        }

                        // Edit-specific rendering
                        if (showAnnotationPopup.type === 'edits') {
                          const parsed = parseEditContent(item.content);
                          const startSeconds = timestampToSeconds(item.start);
                          return (
                            <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 space-y-3">
                              {/* Title if present */}
                              {parsed.title && (
                                <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                                  {parsed.title}
                                </h4>
                              )}

                              {/* Priority badge if present */}
                              {parsed.priority && (
                                <div>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    parsed.priority.toLowerCase().includes('high')
                                      ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                                      : parsed.priority.toLowerCase().includes('medium')
                                        ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300'
                                        : 'bg-[#edf0f4] dark:bg-[#314159] text-[#455468] dark:text-[#d9e2f1]'
                                  }`}>
                                    Priority: {parsed.priority}
                                  </span>
                                </div>
                              )}

                              {/* Main suggestion */}
                              <div className="text-sm text-gray-700 dark:text-gray-300">
                                <span className="font-medium text-gray-900 dark:text-gray-100">Suggestion: </span>
                                {parsed.suggestion}
                              </div>

                              {/* Improvement if present */}
                              {parsed.improvement && (
                                  <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-[#FFB52B] dark:border-[#d5a145]">
                                    <span className="font-medium text-[#8a6115] dark:text-[#ffcf74]">Improvement: </span>
                                    {parsed.improvement}
                                  </div>
                              )}

                              {/* Note if present */}
                              {parsed.note && (
                                <div className="text-xs text-gray-500 dark:text-gray-500 italic">
                                  Note: {parsed.note}
                                </div>
                              )}

                              {/* Timestamp button */}
                              <button
                                onClick={() => {
                                  seekTo(startSeconds);
                                  setShowAnnotationPopup(null);
                                }}
                                className="flex items-center gap-1.5 px-2 py-1 text-xs rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                              >
                                <Clock size={12} />
                                {item.start}
                              </button>
                            </div>
                          );
                        }

                        // Default rendering for clips
                        return (
                          <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                Clip {index + 1}
                              </h4>
                              <button
                                onClick={() => {
                                  seekTo(timestampToSeconds(item.start));
                                  setShowAnnotationPopup(null);
                                }}
                                className="flex items-center gap-1.5 px-2 py-1 text-xs rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                              >
                                <Clock size={12} />
                                {item.start}
                              </button>
                            </div>

                            <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                              {item.title || item.content.replace(/\[CLIP\]\s*/g, '').trim()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800">
                <button
                  onClick={() => setShowAnnotationPopup(null)}
                  className="w-full py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comment Popup */}
      {showCommentPopup && (showCommentPopup.comments.length > 0 || canMutateComments) && (
        <CommentPopup
          comments={showCommentPopup.comments}
          position={showCommentPopup.position}
          onClose={() => setShowCommentPopup(null)}
          onReply={canMutateComments ? async (parentCommentId, content) => {
            try {
              let response: Response;

              if (isPublicShare && shareToken) {
                const headers: Record<string, string> = {
                  'Content-Type': 'application/json',
                };
                const anonymousSessionId = getAnonymousSessionId();
                const anonymousName = typeof window === 'undefined'
                  ? ''
                  : window.localStorage.getItem('anonymousName') || '';

                if (anonymousSessionId) {
                  headers['X-Anonymous-Session-ID'] = anonymousSessionId;
                }

                response = await fetch(`${API_BASE_URL}/api/public/share/${shareToken}/comments`, {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({
                    content,
                    parentCommentId,
                    type: 'comment',
                    anonName: anonymousName.trim() || undefined,
                  }),
                });
              } else {
                response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ 
                    content,
                    parentCommentId
                  }),
                });
              }

              if (response.ok) {
                const threadedComments = await fetchCurrentViewComments();
                if (threadedComments) {
                  // Filter to only include comments relevant to this popup
                  const originalIds = new Set(showCommentPopup.comments.map((c: any) => c.id));
                  const updatedComments = threadedComments.filter((comment: any) => {
                    // Include if it was originally shown
                    if (originalIds.has(comment.id)) return true;
                    // Include if it's in the same time range
                    const inTimeRange = showCommentPopup.comments.some((pc: any) => {
                      if (!comment.timestampSeconds || !pc.timestampSeconds) return false;
                      return Math.abs(comment.timestampSeconds - pc.timestampSeconds) < 0.1;
                    });
                    return inTimeRange;
                  });
                  
                  setShowCommentPopup({
                    ...showCommentPopup,
                    comments: updatedComments
                  });
                }
              }
            } catch (error) {
              console.error('Failed to reply:', error);
            }
          } : undefined}
          onReact={!isPublicShare && canMutateComments ? async (commentId, emoji) => {
            try {
              const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments/${commentId}/reactions`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ emoji }),
              });
              if (response.ok) {
                // Reload reactions
                const reactionsResponse = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/reactions`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                });
                if (reactionsResponse.ok) {
                  await reactionsResponse.json();
                  // Force re-render of popup with new reactions
                  const popupComments = [...showCommentPopup.comments];
                  setShowCommentPopup({
                    ...showCommentPopup,
                    comments: popupComments
                  });
                }
              }
            } catch (error) {
              console.error('Failed to react:', error);
            }
          } : undefined}
          onDelete={!isPublicShare && canMutateComments ? async (commentId) => {
            try {
              const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments/${commentId}`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });
              if (response.ok) {
                // Remove comment from popup without refreshing page
                const updatedComments = showCommentPopup.comments.filter((c: any) => {
                  // Remove the comment and any replies to it
                  const shouldRemove = (comment: any): boolean => {
                    if (comment.id === commentId) return true;
                    if (comment.replies) {
                      comment.replies = comment.replies.filter((r: any) => !shouldRemove(r));
                    }
                    return false;
                  };
                  return !shouldRemove(c);
                });
                
                if (updatedComments.length === 0) {
                  setShowCommentPopup(null);
                } else {
                  setShowCommentPopup({
                    ...showCommentPopup,
                    comments: updatedComments
                  });
                }
                
                // Update local comments
                const commentsResponse = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                });
                if (commentsResponse.ok) {
                  const data = await commentsResponse.json();
                  const threadedComments = organizeCommentsIntoThreads(data.comments || []);
                  setLocalComments(threadedComments);
                }
              }
            } catch (error) {
              console.error('Failed to delete:', error);
            }
          } : undefined}
          onAddComment={canMutateComments ? async (content, timestampSeconds) => {
            try {
              let response: Response;

              if (isPublicShare && shareToken) {
                const headers: Record<string, string> = {
                  'Content-Type': 'application/json',
                };
                const anonymousSessionId = getAnonymousSessionId();
                const anonymousName = typeof window === 'undefined'
                  ? ''
                  : window.localStorage.getItem('anonymousName') || '';

                if (anonymousSessionId) {
                  headers['X-Anonymous-Session-ID'] = anonymousSessionId;
                }

                response = await fetch(`${API_BASE_URL}/api/public/share/${shareToken}/comments`, {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({
                    content,
                    type: 'comment',
                    anonName: anonymousName.trim() || undefined,
                    timestampSeconds: timestampSeconds || showCommentPopup.timestampSeconds
                  }),
                });
              } else {
                response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    content,
                    timestampSeconds: timestampSeconds || showCommentPopup.timestampSeconds
                  }),
                });
              }
              
              if (response.ok) {
                const threadedComments = await fetchCurrentViewComments();
                if (threadedComments) {
                  // Update the popup with the new comments for this timestamp
                  const updatedComments = getCommentsForTimeRange(
                    timestampSeconds || showCommentPopup.timestampSeconds || 0,
                    timestampSeconds || showCommentPopup.timestampSeconds || 0
                  );
                  
                  setShowCommentPopup({
                    ...showCommentPopup,
                    comments: updatedComments
                  });
                }
              }
            } catch (error) {
              console.error('Failed to add comment:', error);
            }
          } : undefined}
          sessionId={!isPublicShare && canMutateComments ? sessionId : undefined}
          token={!isPublicShare && canMutateComments ? token || undefined : undefined}
          readOnly={!canMutateComments}
        />
      )}
      
      {/* AI Results Modal */}
      <AnimatePresence>
      </AnimatePresence>
    </>
  );
};

export default ReadableTranscriptView;
